import type { AgentTopology } from "@/lib/agent-topologies";
import { fetchAuthSession, signOut } from "aws-amplify/auth";
import { getImpersonatedClientId } from "@/lib/impersonation-storage";
import { sortByName } from "@/lib/collation";

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000/api/v1";

// Log the API base URL on initialization (dev only)
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  console.log('[API Client] Initialized with API_BASE_URL:', API_BASE_URL);
}

// Custom error class for API errors
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public data?: any
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// The ApiError message for an error body: a structured detail ({code, message, ...})
// contributes its message, so nothing ever reads "[object Object]".
function errorDetailMessage(data: any): string {
  if (typeof data?.detail?.message === "string") return data.detail.message;
  return data?.detail || "Request failed";
}

// Helper function to format API error messages
export function formatApiError(error: ApiError): string {
  // If data.detail is a string, return it
  if (typeof error.data?.detail === 'string') {
    return error.data.detail;
  }

  // A structured detail ({code, message, ...}) carries its own human-readable message
  if (typeof error.data?.detail?.message === 'string') {
    return error.data.detail.message;
  }

  // If data.detail is an array (Pydantic validation errors), format them
  if (Array.isArray(error.data?.detail)) {
    return error.data.detail
      .map((err: any) => {
        const field = err.loc?.slice(1).join('.') || 'Unknown field';
        return `${field}: ${err.msg}`;
      })
      .join('; ');
  }

  // Fallback to the error message
  return error.message;
}

export interface PmsState {
  source: string;
  paused: boolean;
  reason: string | null;
  force_voice_shadow: boolean;
  paused_by: string | null;
  paused_at: string | null;
  active_clinics: number;
  clinics_with_sync_error: number;
  last_synced_at: string | null;
  unreachable_clinics: number;
}

export interface PmsClinic {
  client_id: string;
  name: string;
  agent_status: string;
  transfer_phone: string | null;
  can_take_voice_shadow: boolean;
}

const GESDEN_DB_CHECK_TIMEOUT_MS = 60000;

// Track app initialization time to avoid false positive session expiration
const APP_START_TIME = Date.now();
const POST_LOGIN_GRACE_PERIOD_MS = 5000; // 5 seconds after app start

// Handle session expiration by redirecting to login
async function handleSessionExpired(): Promise<void> {
  // Don't redirect if we just loaded the app (likely post-login race condition)
  const timeSinceAppStart = Date.now() - APP_START_TIME;
  if (timeSinceAppStart < POST_LOGIN_GRACE_PERIOD_MS) {
    console.warn("Token not available yet, but within grace period. Not treating as expired.");
    return;
  }

  try {
    await signOut();
  } catch (e) {
    // Ignore signOut errors, we're redirecting anyway
  }

  if (typeof window !== "undefined") {
    const currentPath = window.location.pathname + window.location.search;
    window.location.href = `/signin?redirect=${encodeURIComponent(currentPath)}&expired=true`;
  }
}

// Amplify wraps a browser fetch failure (offline, DNS down, connection reset) into
// an AmplifyError named "NetworkError" - this is how we tell "couldn't reach Cognito"
// apart from "Cognito rejected the token", which must NOT be treated as a real
// session expiration (e.g. a laptop waking from sleep before Wi-Fi reassociates).
export function isNetworkError(error: unknown): boolean {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  return (error as { name?: string } | null | undefined)?.name === "NetworkError";
}

// Resolves once the browser reports connectivity again, or after timeoutMs - whichever
// comes first. Lets a request pause through a brief offline blip instead of giving up.
export function waitForOnline(timeoutMs: number): Promise<void> {
  if (typeof window === "undefined" || navigator.onLine) return Promise.resolve();

  return new Promise((resolve) => {
    const onOnline = () => {
      clearTimeout(timer);
      window.removeEventListener("online", onOnline);
      resolve();
    };
    const timer = setTimeout(() => {
      window.removeEventListener("online", onOnline);
      resolve();
    }, timeoutMs);
    window.addEventListener("online", onOnline);
  });
}

// Get Cognito token from Amplify with retry logic for post-login race conditions.
// Reports whether any failure was network-related so callers can avoid treating a
// dead connection as a genuinely expired session.
async function getCognitoToken(
  retries = 3,
  delayMs = 300,
  forceRefresh = false
): Promise<{ token: string; networkError: boolean }> {
  let networkError = false;

  for (let i = 0; i < retries; i++) {
    try {
      const { tokens } = await fetchAuthSession({ forceRefresh: forceRefresh || i > 0 });
      const token = tokens?.idToken?.toString() || "";

      if (token) {
        return { token, networkError: false };
      }

      // No token yet - wait before retry (unless last attempt)
      if (i < retries - 1) {
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    } catch (error) {
      if (isNetworkError(error)) {
        networkError = true;
      }
      console.warn(`Token retrieval attempt ${i + 1} failed:`, error);

      // Wait before retry (unless last attempt)
      if (i < retries - 1) {
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }
  }

  return { token: "", networkError };
}

// Fetch with timeout helper
async function fetchWithTimeout(
  url: string,
  options: RequestInit = {},
  timeoutMs: number = 30000
): Promise<Response> {
  const controller = new AbortController();
  // A caller's own signal (a superseded load) aborts the fetch too, surfacing as an AbortError rather than a timeout.
  const callerSignal = options.signal;
  const abortWithCaller = () => controller.abort();
  if (callerSignal?.aborted) controller.abort();
  callerSignal?.addEventListener("abort", abortWithCaller, { once: true });

  console.log(`[API] Fetching: ${url}`, {
    method: options.method || 'GET',
    hasAuth: options.headers ? 'Authorization' in (options.headers as any) : false,
    timeoutMs,
  });

  const fetchPromise = fetch(url, {
    ...options,
    signal: controller.signal,
  }).then((response) => {
    console.log(`[API] Response from ${url}:`, {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });
    return response;
  });

  // Use Promise.race so the timeout fires even if fetch hangs without rejecting
  // (e.g. a browser extension closes its message channel without responding).
  // AbortController alone only works when fetch rejects — it can't abort a
  // promise that silently stalls.
  const timeoutPromise = new Promise<never>((_, reject) =>
    setTimeout(() => {
      controller.abort();
      reject(new ApiError(408, `Request timeout after ${timeoutMs}ms`));
    }, timeoutMs)
  );

  try {
    return await Promise.race([fetchPromise, timeoutPromise]);
  } catch (error: any) {
    // The caller cancelled it on purpose (a superseded load): expected, so neither logged nor a timeout.
    if (callerSignal?.aborted) throw error;

    // Always log errors for debugging, even in production
    console.error(`[API] Error fetching ${url}:`, error);

    if (error instanceof ApiError) throw error;
    if (error.name === 'AbortError') {
      throw new ApiError(408, `Request timeout after ${timeoutMs}ms`);
    }
    throw error;
  } finally {
    callerSignal?.removeEventListener("abort", abortWithCaller);
  }
}

// Public API request (no authentication required)
//
// Type Safety Note:
// For endpoints that return 204 No Content or may return empty response bodies,
// explicitly type the call as publicApiRequest<null> to ensure type safety.
// Example: await publicApiRequest<null>('/resource', { method: 'DELETE' })
//
// The function will return null for:
// - 204 No Content responses (common for DELETE operations)
// - Successful responses (2xx) with empty or invalid JSON bodies
export async function publicApiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...options.headers,
  };

  const response = await fetchWithTimeout(url, {
    ...options,
    headers,
  });

  // Handle 204 No Content (DELETE operations typically return this)
  if (response.status === 204) {
    return null as T;
  }

  let data;
  try {
    data = await response.json();
  } catch (e) {
    // Log JSON parsing errors for debugging
    console.error(`Failed to parse JSON response from ${endpoint}:`, e);

    // For error responses, provide context about the parsing failure
    if (!response.ok) {
      throw new ApiError(
        response.status,
        `Request failed (status ${response.status}) and response was not valid JSON`,
        { parseError: String(e) }
      );
    }

    // For successful responses with empty/invalid JSON body, treat as No Content
    return null as T;
  }

  if (!response.ok) {
    throw new ApiError(
      response.status,
      errorDetailMessage(data),
      data
    );
  }

  return data as T;
}

// Authenticated API request (requires Cognito token)
//
// Type Safety Note:
// For endpoints that return 204 No Content or may return empty response bodies,
// explicitly type the call as apiRequest<null> to ensure type safety.
// Example: await apiRequest<null>('/users/123', { method: 'DELETE' })
//
// The function will return null for:
// - 204 No Content responses (common for DELETE operations)
// - Successful responses (2xx) with empty or invalid JSON bodies
async function apiRequestCore<T>(
  endpoint: string,
  options: RequestInit = {},
  timeoutMs?: number
): Promise<{ data: T; response: Response }> {
  const url = `${API_BASE_URL}${endpoint}`;

  let { token, networkError } = await getCognitoToken();

  // A dead connection (laptop asleep, switching Wi-Fi networks) looks identical to a
  // missing token here. Give connectivity a chance to return before giving up, so we
  // don't sign out a user whose session is perfectly valid.
  if (!token && networkError) {
    await waitForOnline(10_000);
    ({ token, networkError } = await getCognitoToken(3, 300, true));
  }

  if (!token) {
    if (networkError) {
      throw new ApiError(0, "Network unavailable. Please check your connection and try again.");
    }
    await handleSessionExpired();
    throw new ApiError(401, "Session expired");
  }

  // Get impersonated client ID only for admin users (security check)
  let impersonatedClientId: string | null = null;
  try {
    const { tokens } = await fetchAuthSession();
    const groups = (tokens?.accessToken?.payload['cognito:groups'] as string[]) || [];
    const isAdmin = groups.includes('admin');
    if (isAdmin) {
      impersonatedClientId = getImpersonatedClientId();
    }
  } catch {
    // If we can't verify admin status, don't add the header (fail secure)
  }

  const headers: HeadersInit = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    ...(impersonatedClientId && { "X-Client-Id": impersonatedClientId }),
    ...options.headers,
  };

  let response = await fetchWithTimeout(
    url,
    {
      ...options,
      headers,
    },
    timeoutMs
  );

  // A single 401 can be a transient Cognito/network blip rather than a
  // genuinely expired session. Retry with a forced token refresh - same
  // retry strength (3 attempts, 300ms apart) as the AuthContext hydration
  // check in fbd8459 - before treating it as real expiry.
  if (response.status === 401) {
    const { token: refreshedToken, networkError: refreshNetworkError } = await getCognitoToken(3, 300, true);
    if (refreshedToken && refreshedToken !== token) {
      response = await fetchWithTimeout(
        url,
        {
          ...options,
          headers: { ...headers, Authorization: `Bearer ${refreshedToken}` },
        },
        timeoutMs
      );
    } else if (refreshNetworkError) {
      throw new ApiError(0, "Network unavailable. Please check your connection and try again.");
    }
  }

  // Handle 401 Unauthorized - session expired or invalid token
  if (response.status === 401) {
    await handleSessionExpired();
    throw new ApiError(401, "Session expired");
  }

  // Handle 204 No Content (DELETE operations typically return this)
  if (response.status === 204) {
    return { data: null as T, response };
  }

  let data;
  try {
    data = await response.json();
  } catch (e) {
    // Log JSON parsing errors for debugging
    console.error(`Failed to parse JSON response from ${endpoint}:`, e);

    // For error responses, provide context about the parsing failure
    if (!response.ok) {
      throw new ApiError(
        response.status,
        `Request failed (status ${response.status}) and response was not valid JSON`,
        { parseError: String(e) }
      );
    }

    // For successful responses with empty/invalid JSON body, treat as No Content
    return { data: null as T, response };
  }

  if (!response.ok) {
    throw new ApiError(
      response.status,
      errorDetailMessage(data),
      data
    );
  }

  return { data: data as T, response };
}

/**
 * Request options that make one call act for a named client instead of the caller's own.
 *
 * Only meaningful for an admin and only for routes that resolve their tenant via
 * `get_client_id`. Returns `{}` for an undefined id so a single call site can serve both the
 * clinic-facing and admin uses.
 *
 * Note the asymmetry with the sidebar's impersonation header below, which is suppressed for
 * non-admins: this one rides `options.headers` and is therefore sent whatever the caller's
 * role. That is safe because `get_client_id` honours `X-Client-Id` only for an admin and
 * otherwise falls back to the caller's own client — the API is the boundary, not the
 * browser. Every caller that passes a client id is on an `isAdmin()`-gated page; if that
 * ever stops being true, the page is what has to prevent it.
 */
function asClient(clientId?: string): RequestInit {
  return clientId ? { headers: { "X-Client-Id": clientId } } : {};
}

/**
 * The API's default order is `sort_order`, which nothing sets — effectively insertion order.
 * Every list a person picks from should read alphabetically instead.
 */
function sortPageByName<T extends { name: string }>(page: Paginated<T>): Paginated<T> {
  return { ...page, items: sortByName(page.items ?? []) };
}

export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  timeoutMs?: number
): Promise<T> {
  const { data } = await apiRequestCore<T>(endpoint, options, timeoutMs);
  return data;
}

// Like apiRequest, but also returns the raw Response headers — for callers
// that need a server-set header (e.g. a polling cursor timestamp) alongside
// the parsed body, without every caller paying for header access.
export async function apiRequestWithHeaders<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<{ data: T; headers: Headers }> {
  const { data, response } = await apiRequestCore<T>(endpoint, options);
  return { data, headers: response.headers };
}

// Partner Portal types
export type PartnerLeadStatus =
  | "submitted"
  | "in_progress"
  | "signed"
  | "rejected"
  | "expired";

export interface PartnerProfile {
  id: string;
  name: string;
  email: string;
  /** Decides the role-driven copy on the partner portal. */
  kind: "partner" | "clinic";
  /** Vanity handle of the referral link; minted by the API on the first /partner/me. */
  slug: string | null;
  /** `https://toniagent.com/p/<slug>`, or null until a slug exists. */
  referral_url: string | null;
  /** Percentage of each of a referred clinic's monthly payments, for 12
   * months; null while not agreed. Always null for a clinic. */
  commission_rate: string | null;
}

/** Admin view of a partner: the profile plus the commission agreed with it. */
export interface AdminPartner extends PartnerProfile {
  commission_rate: string | null;
  client_id: string | null;
}

export interface AdminPartnerUpdate {
  name?: string;
  slug?: string;
  commission_rate?: number;
}

export interface PartnerLead {
  id: string;
  clinic_name: string;
  city: string | null;
  contact_name: string;
  email: string | null;
  phone: string;
  comments: string | null;
  status: PartnerLeadStatus;
  submitted_at: string;
  referral_deadline: string;
  signed_date: string | null;
  rejected_date: string | null;
  plan_name: string | null;
  paid: boolean;
  /** `form`: typed in the portal by the referrer. `link`: the referred clinic
   * arrived through the referral link and filled the demo form itself. */
  source: "form" | "link";
}

/** A lead as the admin sees it: the partner-scoped shape plus who referred it. */
export interface AdminPartnerLead extends PartnerLead {
  partner_id: string;
  partner_kind: "partner" | "clinic";
  client_id: string | null;
}

export interface AdminPartnerLeadUpdate {
  /** `signed` belongs to the client that converted the lead and `expired` is
   * derived from the deadline, so the API refuses both. The panel writes only
   * `rejected` — see `adminLeadEdits`. */
  status?: Exclude<PartnerLeadStatus, "signed" | "expired">;
  /** ISO date. Moving it past today is what revives a lead that reads `expired`. */
  referral_deadline?: string;
  /** ISO date. */
  rejected_date?: string;
}

export interface CreatePartnerLeadRequest {
  clinic_name: string;
  contact_name: string;
  phone: string;
  email?: string;
  comments?: string;
}

/** An admin filing a referral on a partner's behalf. `client_id` names a clinic
 * that is already a client, which files the referral as signed against it. */
export interface CreateAdminPartnerLeadRequest extends CreatePartnerLeadRequest {
  client_id?: string;
}

// Invitation types
export type InvitationStatus = "pending" | "accepted" | "expired" | "revoked";

export interface Invitation {
  id: string;
  email: string;
  group_name: string;
  status: InvitationStatus;
  expires_at: string;
  created_by_email: string | null;
  created_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
  client_id: string | null;
  partner_name?: string | null;
  signup_url?: string;
  token?: string | null;
}

export interface CreateInvitationRequest {
  email: string;
  given_name: string;
  family_name: string;
  group_name: string;
  client_id?: string;
  partner_name?: string;
  partner_commission_rate?: number;
}

export interface ValidateInvitationResponse {
  valid: boolean;
  email?: string;
  group_name?: string;
  given_name?: string;
  family_name?: string;
  client_name?: string;
  status?: string;
  expires_at?: string;
  error?: string;
}

export interface CompleteSignupResponse {
  success: boolean;
  message: string;
  user: {
    email: string;
    role: string;
    client_id: string | null;
  };
}

// Client types
export interface Client {
  id: string;
  name: string;
  nif: string | null;
  address: string | null;
  zip_code: string | null;
  transfer_phone: string | null;
  public_phone: string | null;
  toni_agent_phone: string | null;
  test_phone: string | null;
  whatsapp_phone_number_id: string | null;
  whatsapp_confirmation_requests_enabled: boolean;
  /**
   * The clinic's own closing line inside that reminder — the confirmation-request
   * template's {{other_instructions}} parameter. Stored already collapsed to one line
   * (Meta rejects a template parameter containing newlines). NULL = no line of its own.
   */
  whatsapp_confirmation_instructions: string | null;
  /**
   * How many days before the appointment that reminder goes out, and whether they are
   * counted as days the clinic works (skipping its closures and holidays) or as plain
   * calendar days. Always present: the columns are NOT NULL, defaulting to the 2 working
   * days that used to be a backend constant.
   */
  whatsapp_reminder_lead_days: number;
  whatsapp_reminder_lead_counts_working_days: boolean;
  /**
   * Whether patient messages (reminder, booking created/cancelled) name the professional.
   * FALSE says "our team" instead. Always present: the column is NOT NULL DEFAULT TRUE.
   */
  whatsapp_show_professional_name: boolean;
  /** Meta WABA id granted via the Phase 3 Embedded Signup connect flow. NULL = not connected. */
  whatsapp_waba_id: string | null;
  email: string | null;
  website: string | null;
  description: string | null;
  owner_cognito_id: string;
  owner_email: string | null;
  agent_status: "live" | "shadow" | "off";
  agent_language: string;
  timezone: string | null;
  insurance_enabled: boolean;
  booking_enabled: boolean;
  agenda_readonly: boolean;
  /** Control-panel only: false hides the Agendas view from this clinic's panel. */
  agenda_view_enabled: boolean;
  /** What the NEXT deploy will produce. Runtime reads agent_deployments.agent_topology. */
  agent_topology?: AgentTopology;
  public_health_enabled: boolean;
  hypercare: boolean;
  democalls: boolean;
  testingcalls: boolean;
  development: boolean;
  apply_local_holidays: boolean;
  country: string;
  locality_code: string | null;
  locality_name: string | null;
  multi_location_enabled: boolean;
  is_deleted: boolean;
  maps_url: string | null;
  pinned_builder_version?: string | null;
  /** While this ISO timestamp is in the future, the consecutive-transfer-failures
   *  alert is suppressed for this clinic. Null = alerting as normal. */
  transfer_alerts_paused_until?: string | null;
  transfer_alerts_pause_reason?: string | null;
  created_at: string;
  updated_at: string;
}


export interface CreateClientRequest {
  name: string;
  nif?: string;
  address?: string;
  zip_code?: string;
  transfer_phone?: string;
  public_phone?: string;
  toni_agent_phone?: string;
  test_phone?: string | null;
  whatsapp_phone_number_id?: string | null;
  whatsapp_confirmation_requests_enabled?: boolean;
  whatsapp_confirmation_instructions?: string | null;
  email?: string;
  website?: string;
  description?: string;
  agent_status?: string;
  agent_language?: string;
  country?: string;
  locality_code?: string | null;
  locality_name?: string | null;
  /** plead_ id of the partner lead this clinic converts from. */
  referred_by_lead_id?: string;
  /** Plan the referred clinic signs on; drives the partner's reward. */
  referred_plan_name?: SubscriptionPlan;
}

export interface UpdateClientRequest {
  name?: string;
  nif?: string | null;
  address?: string | null;
  zip_code?: string | null;
  transfer_phone?: string;
  public_phone?: string | null;
  toni_agent_phone?: string | null;
  test_phone?: string | null;
  whatsapp_phone_number_id?: string | null;
  whatsapp_confirmation_requests_enabled?: boolean;
  whatsapp_confirmation_instructions?: string | null;
  whatsapp_reminder_lead_days?: number;
  whatsapp_reminder_lead_counts_working_days?: boolean;
  whatsapp_show_professional_name?: boolean;
  email?: string | null;
  website?: string | null;
  description?: string | null;
  agent_status?: "live" | "shadow" | "off";
  agent_language?: string;
  timezone?: string;
  hypercare?: boolean;
  democalls?: boolean;
  testingcalls?: boolean;
  development?: boolean;
  agenda_readonly?: boolean;
  agenda_view_enabled?: boolean;
  agent_topology?: AgentTopology;
  apply_local_holidays?: boolean;
  country?: string;
  locality_code?: string | null;
  locality_name?: string | null;
  multi_location_enabled?: boolean;
  is_deleted?: boolean;
  maps_url?: string | null;
  transfer_alerts_paused_until?: string | null;
  transfer_alerts_pause_reason?: string | null;
}

export interface UpdateClientWithSubscriptionRequest {
  client_data: UpdateClientRequest;
  subscription_data: CreateSubscriptionRequest;
}

export interface ClientWithSubscriptionResponse {
  client: Client;
  subscription: Subscription;
}

// Agent Configuration types
export type AvailabilityType =
  | "always"
  | "outside_business_hours"
  | "outside_business_hours_with_fallback"
  | "custom";
export type Weekday = "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday" | "sunday";

export interface DaySchedule {
  start: string | null;              // HH:MM format or null if off
  end: string | null;                // HH:MM format or null if off
  midday_break_start?: string | null; // HH:MM format or null if no midday break
  midday_break_end?: string | null;   // HH:MM format or null if no midday break
}

export interface BusinessHours {
  monday?: DaySchedule | null;
  tuesday?: DaySchedule | null;
  wednesday?: DaySchedule | null;
  thursday?: DaySchedule | null;
  friday?: DaySchedule | null;
  saturday?: DaySchedule | null;
  sunday?: DaySchedule | null;
}

export interface Location {
  id: string;
  /** The id the agent's knowledge base and tool calls use (`loc_662f757f`); searchable. */
  short_ref?: string;
  client_id: string;
  name: string;
  is_active: boolean;
  is_primary: boolean;
  sort_order: number;
  address: string | null;
  phone: string | null;
  /** Free-text notes surfaced to the voice agent's knowledge base. */
  description: string | null;
  timezone: string;
  business_hours: BusinessHours;
  apply_local_holidays: boolean;
  country: string;
  locality_code: string | null;
  locality_name: string | null;
  source: string;
  external_id: string | null;
  maps_url: string | null;
  /** Locations sharing a group are searched together; null when not grouped. */
  location_group_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface LocationGroup {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface CreateLocationRequest {
  name: string;
  address?: string | null;
  phone?: string | null;
  description?: string | null;
  timezone?: string;
  business_hours?: BusinessHours | null;
  apply_local_holidays?: boolean;
  country?: string;
  locality_code?: string | null;
  locality_name?: string | null;
  sort_order?: number;
  maps_url?: string | null;
  location_group_id?: string | null;
}

export interface UpdateLocationRequest {
  name?: string;
  address?: string | null;
  phone?: string | null;
  description?: string | null;
  timezone?: string;
  business_hours?: BusinessHours | null;
  apply_local_holidays?: boolean;
  country?: string;
  locality_code?: string | null;
  locality_name?: string | null;
  is_active?: boolean;
  sort_order?: number;
  maps_url?: string | null;
  /** Explicit null takes the location out of its group. */
  location_group_id?: string | null;
}

export interface CustomScheduleEntry {
  id: string;
  from_day: Weekday;
  from_time: string; // HH:MM format
  to_day: Weekday;
  to_time: string;   // HH:MM format
}

export interface AgentAvailabilityConfig {
  availability_type: AvailabilityType;
  custom_schedules?: CustomScheduleEntry[] | null;
}

export interface AgentConfig {
  agent_status: "live" | "shadow" | "off";
  play_recording_notice: boolean;
  enable_call_recording: boolean;
  use_toniagent_caller_id: boolean;
  announce_out_of_hours_message: boolean;
  record_out_of_hours_voicemail: boolean;
  insurance_enabled: boolean;
  /** Insurance is knowledge-base information only: the agent never asks for the payment type. */
  insurance_informational_only: boolean;
  match_patient_by_name_when_phone_unknown: boolean;
  // Optional until every backend serving this build returns it.
  match_patient_by_name_two_tokens?: boolean;
  /** findBookings asks for the patient's DNI once the phone and date searches miss.
   * Optional until every backend serving this build returns it. */
  patient_dni_lookup_enabled?: boolean;
  /** Ask the DNI/NIE at booking when the patient's chart has none. Forced on while RadioWeb
   * is active. Optional until every backend serving this build returns it. */
  patient_dni_at_booking_enabled?: boolean;
  /** Slot search anchors a candidate grid at each free gap, so a gap between two
   * appointments that the agenda's cadence never lands on becomes bookable. */
  offer_gap_slots_enabled: boolean;
  ask_location_only_when_ambiguous: boolean;
  public_health_enabled: boolean;
  cip_enabled: boolean;
  /** Furthest future day (in days from today) the agent may offer. null = no limit. */
  booking_horizon_days: number | null;
  sms_notification_enabled: boolean;
  has_recording_notice_audio: boolean;
  agent_language: string | null;
  agent_languages?: string[];
  catalan_is_valencian?: boolean;
  timezone: string | null;
  availability_config: AgentAvailabilityConfig | null;
  business_hours: BusinessHours;
  apply_local_holidays: boolean;
  country: string;
  locality_code: string | null;
  locality_name: string | null;
  agent_voice_gender: "male" | "female";
  // null = the voice's default persona name (female: Marta, male: Toni).
  agent_name: string | null;
  // null = default_new_caller_greeting, the standard greeting in the agent's main language.
  new_caller_greeting: string | null;
  default_new_caller_greeting: string;
  known_caller_greeting_example: string;
}

export interface UpdateAgentConfigRequest {
  agent_status?: "live" | "shadow" | "off";
  play_recording_notice?: boolean;
  enable_call_recording?: boolean;
  use_toniagent_caller_id?: boolean;
  announce_out_of_hours_message?: boolean;
  record_out_of_hours_voicemail?: boolean;
  insurance_enabled?: boolean;
  insurance_informational_only?: boolean;
  match_patient_by_name_when_phone_unknown?: boolean;
  match_patient_by_name_two_tokens?: boolean;
  patient_dni_lookup_enabled?: boolean;
  patient_dni_at_booking_enabled?: boolean;
  offer_gap_slots_enabled?: boolean;
  ask_location_only_when_ambiguous?: boolean;
  public_health_enabled?: boolean;
  cip_enabled?: boolean;
  booking_horizon_days?: number | null;
  sms_notification_enabled?: boolean;
  agent_language?: string;
  agent_languages?: string[];
  catalan_is_valencian?: boolean;
  timezone?: string;
  availability_config?: AgentAvailabilityConfig;
  business_hours?: BusinessHours;
  apply_local_holidays?: boolean;
  country?: string;
  locality_code?: string | null;
  locality_name?: string | null;
  agent_voice_gender?: "male" | "female";
  agent_name?: string | null;
  new_caller_greeting?: string | null;
}

// Holiday selector + preview types
export interface UpcomingHoliday {
  date: string;
  name: string | null;
  scope: string;
}

export interface HolidayProvinceOption {
  key: string;
  name: string;
}

export interface LocalityOption {
  code: string;
  name: string;
}

// Twilio Phone Number types
export interface TwilioPhoneNumber {
  phone_number: string;
  friendly_name: string;
  assigned: boolean;
}

export interface TwilioPhoneNumberListResponse {
  phone_numbers: TwilioPhoneNumber[];
}

/**
 * An ElevenLabs WhatsApp account in the assignable pool.
 *
 * `phone_number_id` is Meta's identifier — the value stored on the client and the
 * only field safe to match on. `phone_number` is a display string and arrives
 * space-formatted (e.g. "+34 930 34 82 80"), not E.164.
 */
export interface WhatsAppAccount {
  phone_number_id: string;
  phone_number: string;
  display_name: string;
  assigned: boolean;
  assigned_client_id: string | null;
  assigned_client_name: string | null;
  is_token_expired: boolean;
}

export interface WhatsAppAccountListResponse {
  accounts: WhatsAppAccount[];
}

/** How far a clinic's ToniAgent number has got towards being on our own WABA. */
export interface WhatsAppNumberProvisioningState {
  state: "requested" | "code_sent" | "verified" | "failed";
  attempts: number;
  last_error: string | null;
  verified_name: string;
  code_sent_at: string | null;
  verified_at: string | null;
  /**
   * When another verification code may be requested. Meta rate-limits this across the
   * WHOLE WhatsApp account, so a retry inside the window prolongs the block for every
   * clinic — the button must be disabled, not merely allowed to fail.
   */
  retry_after: string | null;
}

/**
 * `on_waba` and `imported_into_elevenlabs` are TRI-STATE.
 *
 * `null` means the lookup failed (Meta or ElevenLabs unreachable), which is not the same
 * as `false`. Treating null as "not there" would offer to add a number that may already
 * exist, and Meta rejects a duplicate with an error that reads like a bug.
 */
export interface WhatsAppNumberStatus {
  phone_number: string | null;
  on_waba: boolean | null;
  phone_number_id: string | null;
  verified_name: string | null;
  code_verification_status: string | null;
  /** Meta's operational state: CONNECTED / PENDING / DISCONNECTED / FLAGGED. */
  connection_status: string | null;
  /**
   * Whether the number is finished and usable, decided by the backend.
   *
   * Do NOT substitute `code_verification_status === "VERIFIED"`: Meta reports EXPIRED on
   * numbers that are live and connected, so that check calls a working production number
   * unverified and offers to "fix" it by requesting another code.
   */
  is_live: boolean | null;
  /** Meta reviews display names separately; PENDING_REVIEW never blocks messaging. */
  name_status: string | null;
  imported_into_elevenlabs: boolean | null;
  provisioning: WhatsAppNumberProvisioningState | null;
}

/**
 * A clinic staff member: who gets a WhatsApp after a call, and who may manage the
 * clinic through the WhatsApp agent. The two are independent — `notify_on_calls` is the
 * notification toggle, the `can_manage_agent`/`agent_access_*` block is the
 * authorization, and neither one implies the other.
 */
export interface WhatsAppManager {
  id: string;
  client_id: string;
  name: string;
  phone_e164: string;
  /**
   * Whether this person gets a WhatsApp when a call finishes. Pairs with
   * `action_required_only`, which narrows that to calls needing staff action.
   */
  notify_on_calls: boolean;
  /** @deprecated Mirror of `notify_on_calls`; the backend drops it next deploy. */
  is_active: boolean;
  /** True (the default) = only calls that left work for the clinic; false = every call. */
  action_required_only: boolean;
  /**
   * Granted at least once — which is on creation for anyone added since staff creation
   * started granting it. Stays true after a revoke; `agent_access_revoked_at` is the record.
   */
  can_manage_agent: boolean;
  /**
   * The live on/off state — false after a revoke, unlike `can_manage_agent`, which
   * records that access was granted at some point. Both must be true to authorize:
   * see `agentAccess.ts`, which mirrors the backend predicate.
   */
  agent_access_active: boolean;
  /** Prefixed ("prof_<uuid>") id of the professional this person IS, or null for e.g. reception. */
  professional_id: string | null;
  /** Email of the owner/admin who granted the access. */
  agent_access_granted_by: string | null;
  agent_access_granted_at: string | null;
  /** ISO 8601. Null = until revoked. Past = access has lapsed. */
  agent_access_expires_at: string | null;
  agent_access_revoked_at: string | null;
  created_at: string;
  updated_at: string;
}

/** One row of the admin WhatsApp send log. */
export interface WhatsAppMessageLog {
  id: string;
  client_id: string;
  client_name: string;
  whatsapp_manager_id: string | null;
  manager_name: string | null;
  recipient_phone: string;
  recipient_name: string | null;
  sender_phone_number_id: string;
  template_name: string;
  template_language_code: string;
  /** 'sent' = accepted by ElevenLabs. There is no delivery signal from the provider. */
  status: string;
  call_id: string | null;
  appointment_origin: string | null;
  appointment_start_at: string | null;
  provider_conversation_id: string | null;
  error_code: string | null;
  error_message: string | null;
  attempted_at: string | null;
  created_at: string;
  /** The rendered template values actually sent. */
  template_params: unknown[] | Record<string, unknown> | null;
  /**
   * The recipient's own turns in the ElevenLabs conversation this send opened. The
   * provider gives no delivery receipt, so a reply is the only proof the message arrived.
   */
  reply_count: number;
  conversation_ended_at: string | null;
  conversation_summary: string | null;
  /**
   * What the patient answered about the appointment this send was about, by tapping a
   * reminder button. Null when they never did — a free-text reply is not an answer.
   */
  patient_response: WhatsAppPatientResponse | null;
  patient_responded_at: string | null;
}

/** Outcome of retrying one failed send. */
export interface WhatsAppMessageRetryResult {
  message_id: string;
  status: string;
  provider_conversation_id?: string | null;
  error_code?: string | null;
  error_message?: string | null;
}

export type WhatsAppPatientResponse = "confirmed" | "declined";

export type WhatsAppPatientResponseFilter = WhatsAppPatientResponse | "no_response";

export interface WhatsAppMessageLogFilters {
  client_id?: string;
  whatsapp_manager_id?: string;
  status?: string;
  response?: WhatsAppPatientResponseFilter;
  template_name?: string;
  /** The recipient's name, or any part of their phone in any format. */
  search?: string;
  /** ISO 8601 instants bounding `created_at`; both inclusive. */
  start_date?: string;
  end_date?: string;
  skip?: number;
  limit?: number;
}

/**
 * One row of the admin SMS log. `status` is Twilio's latest delivery report: only
 * `delivered` means the handset got it; `undelivered`/`failed` carry Twilio's `error_code`.
 */
export interface SmsMessageLog {
  id: string;
  client_id: string;
  client_name: string;
  status: string;
  created_at: string;
  /** Null when Twilio's API refused the message outright, so it never got one. */
  twilio_message_sid: string | null;
  messaging_service_sid: string | null;
  recipient_phone: string | null;
  body: string | null;
  error_code: string | null;
  error_message: string | null;
  status_updated_at: string | null;
}

export interface SmsMessageLogFilters {
  client_id?: string;
  status?: string;
  /** Any part of the recipient's phone in any format. */
  search?: string;
  /** ISO 8601 instants bounding `created_at`; both inclusive. */
  start_date?: string;
  end_date?: string;
  skip?: number;
  limit?: number;
}

export interface MonthlyStatsRow {
  id: string;
  client_id: string;
  client_name: string;
  period_start: string;
  period_end: string;
  billing_start: string | null;
  billing_end: string | null;
  agent_mode_at_period_end: string | null;
  professionals_real: number;
  professionals_billed: number | null;
  mrr_invoiced_eur: string | null;
  setup_revenue_eur: string | null;
  voice_minutes_total: string;
  calls_handled: number;
  /**
   * `calls_handled` split by who took the call. Null for windows stamped before
   * definition_version 2 — frozen rows are never recomputed, so a null means "predates
   * the breakdown", not zero.
   */
  calls_agent_handled: number | null;
  calls_clinic_handled: number | null;
  calls_voicemail: number | null;
  calls_auto_message: number | null;
  unique_callers: number;
  calls_transferred_human: number;
  appointment_requests: number;
  bookings_by_agent: number;
  bookings_cancelled_by_agent: number;
  information_requests: number;
  reminders_sent: number | null;
  whatsapp_interactions: number | null;
  definition_version: number;
  computed_at: string;
}

export type WhatsAppTemplateComponent = "header" | "body" | "button";

/**
 * One value a send of a template must supply, read off the template's Meta components.
 * `name` is null for a POSITIONAL placeholder ({{1}}) — always the case for a URL button's
 * suffix, which is addressed by `button_index` instead.
 */
export interface WhatsAppTemplateParameterSlot {
  component: WhatsAppTemplateComponent;
  name: string | null;
  button_index: number | null;
}

export interface WhatsAppSendParameter extends WhatsAppTemplateParameterSlot {
  value: string;
}

export interface WhatsAppSendRequest {
  client_id: string;
  whatsapp_phone_number_id: string;
  recipient_phone: string;
  template_name: string;
  template_language_code: string;
  agent_id: string;
  parameters: WhatsAppSendParameter[];
}

export interface WhatsAppSendResult {
  message_id: string;
  status: string;
  provider_conversation_id: string | null;
  error_code: string | null;
  /** Provider error verbatim — e.g. Meta's "#132001 … does not exist in ca". */
  error_message: string | null;
}

/**
 * Adding somebody to the staff list also GRANTS them clinic-manager agent access and sends
 * them the welcome WhatsApp — there is no separate grant step for a new person. So the
 * response comes back with `agent_access_active` true, and this request needs no field for it.
 */
export interface CreateWhatsAppManagerRequest {
  name: string;
  /** Any reasonable form; the backend normalises to E.164 using the clinic country. */
  phone: string;
  /** Defaults to false server-side: the credential is not the same as a per-call feed. */
  notify_on_calls?: boolean;
  /** Defaults to true server-side. */
  action_required_only?: boolean;
}

export interface UpdateWhatsAppManagerRequest {
  name?: string;
  phone?: string;
  /**
   * The API accepts `is_active` as an alias for this for one deploy, so either build
   * order is safe. Unknown body fields are silently ignored rather than rejected
   * (tech-stack.md rule 5), which is why that alias exists rather than a version check —
   * without it a toggle from the wrong build would no-op with no error to show.
   */
  notify_on_calls?: boolean;
  /** The API accepts `important_only` as an alias for this too, for the same reason. */
  action_required_only?: boolean;
}

/**
 * Grant or revoke one staff member's access to the WhatsApp clinic-manager agent.
 * Its own request (and its own owner-only route) rather than fields on the update
 * above: this grants a credential, not a contact detail.
 */
export interface SetAgentAccessRequest {
  granted: boolean;
  /** Prefixed "prof_<uuid>", so the agent can resolve "my agenda". Null = not a professional. */
  professional_id?: string | null;
  /** ISO 8601. Null = until revoked. */
  expires_at?: string | null;
}

export type WhatsAppTemplateStatus =
  | "PENDING"
  | "APPROVED"
  | "REJECTED"
  | "PAUSED"
  | "DISABLED"
  | "IN_APPEAL"
  | "FLAGGED";

/** Progress of a template sync. `idle` means this WABA has never been synced. */
export type WhatsAppTemplateSyncStatus =
  | "idle"
  | "queued"
  | "running"
  | "succeeded"
  | "failed";

/**
 * Verdict of the last template sync for our WABA.
 *
 * The sync runs on the worker, not in the request that asks for it — pushing the registry
 * makes one Meta call per template, well past the API Gateway's 30s ceiling. So Meta's
 * rejection report arrives here, in `detail`, rather than as an error response.
 */
export interface WhatsAppTemplateSyncRun {
  status: WhatsAppTemplateSyncStatus;
  detail: string | null;
  started_at: string | null;
  finished_at: string | null;
}

/**
 * A WhatsApp message template mirrored from Meta. Content is defined in code
 * (control-panel-api's template registry), not authored here — there is no create
 * request; `syncWhatsAppTemplates()` enqueues a push of whatever's missing.
 */
export interface WhatsAppTemplate {
  id: string;
  waba_id: string;
  name: string;
  language: string;
  category: string;
  status: WhatsAppTemplateStatus;
  rejected_reason: string | null;
  quality_score: string | null;
  created_at: string;
  updated_at: string;
  /** The placeholders a send must fill, in render order. Empty for a static template. */
  parameters: WhatsAppTemplateParameterSlot[];
}

export type ClientAgentRole = "working-hours" | "off-hours" | "whatsapp-router" | "clinic-manager";

/** One of a clinic's currently deployed ElevenLabs agents. */
export interface ClientAgent {
  role: ClientAgentRole;
  agent_id: string;
  /** The ElevenLabs agent name; falls back to the id when the deploy stored none. */
  name: string;
}

export interface ConnectWhatsAppWabaRequest {
  /**
   * Single-use Embedded Signup authorization code (~30s TTL) from the FB.login callback.
   * Exchanging it server-side is what actually grants access; without it the connection
   * records an id that authorises nothing.
   */
  code?: string;
  /** What the popup reported. A cross-check only — the server re-derives the real WABA. */
  waba_id?: string;
  /**
   * Only the coexistence flow reports a number — the standard flow is WABA-scoped and never
   * learns one. It is the key every per-number Meta call is addressed by.
   */
  phone_number_id?: string;
  /**
   * Which flow the popup ran, derived from its FINISH event. Client-supplied like `waba_id`,
   * so the server stores it as a claim and corroborates it against Meta separately.
   */
  onboarding_flow?: "standard" | "coexistence";
}

export interface ConnectWhatsAppWabaResult {
  success: boolean;
  /** Authoritative, derived server-side from Meta rather than from the browser. */
  waba_id: string;
  /** False means connected but our app is not subscribed to that WABA's webhooks, so
   *  template statuses will only refresh on an explicit sync. */
  subscribed: boolean;
}

export interface OwnWhatsAppTemplate {
  name: string;
  language: string;
  category: string;
  status: string;
  /** Only populated when status is REJECTED — Meta leaves a stale reason behind otherwise. */
  rejected_reason: string | null;
  /** Meta's copy no longer matches the content this codebase defines. */
  drifted: boolean;
}

export interface DisconnectWhatsAppWabaResult {
  /** False when the credential could not be invalidated at Meta. The local connection is
   *  gone either way — but the UI must not claim a clean removal it didn't achieve.
   *  Even when true, only the clinic can remove us from its Meta partner list. */
  revoked_at_meta: boolean;
  /** How many ToniAgent templates were removed from the WABA. Always 0 when the clinic
   *  chose to keep them, which is what distinguishes "kept" from "removed none". */
  templates_deleted: number;
}

export interface WhatsAppWabaStatus {
  /** Whether we hold a stored credential for this clinic's WABA. Note this reflects
   *  local state — Meta can revoke the grant without invalidating the token. */
  connected: boolean;
  waba_id: string | null;
  subscribed: boolean;
  sync_error: string | null;
  phone_number_id: string | null;
  /** What the clinic ran, per the popup — not corroborated by Meta. */
  coexistence: boolean;
}

// Subscription types
export type SubscriptionPlan = "Starter" | "Starter Pro" | "Clinic" | "Clinic Pro" | "Enterprise";
export type SubscriptionStatus = "trial" | "active" | "suspended" | "cancelled";
export type BillingCycle = "monthly" | "yearly";

export interface Subscription {
  id: string;
  client_id: string;
  plan_name: SubscriptionPlan;
  status: SubscriptionStatus;
  minutes_total: number;
  minutes_used: number;
  billing_cycle: BillingCycle;
  price_per_minute: string | null; // Decimal as string from backend
  minutes_reset_start: string; // Date string YYYY-MM-DD
  minutes_reset_end: string; // Date string YYYY-MM-DD
  billing_start: string | null; // Date string YYYY-MM-DD
  billing_end: string | null; // Date string YYYY-MM-DD
  is_active: boolean; // Changed from is_deleted to match backend
  created_at: string;
  updated_at: string;
  // Computed fields from backend
  usage_percentage: number; // 0-100, capped at 100
  is_over_limit: boolean; // True if minutes_used >= minutes_total
  days_until_reset: number; // Days remaining (can be negative if past)
  // The cycle's calls and charged minutes per agent mode; sums to minutes_used. Optional
  // until the control-panel-api that sends it has deployed.
  usage_by_agent_mode?: AgentModeUsage[];
}

export interface AgentModeUsage {
  agent_mode: string;
  calls: number;
  minutes: number;
}

export interface CreateSubscriptionRequest {
  plan_name: SubscriptionPlan;
  status: SubscriptionStatus;
  minutes_total: number;
  billing_cycle?: BillingCycle; // Defaults to "monthly"
  price_per_minute?: string; // Optional, auto-filled from plan
  minutes_reset_start: string; // Date string YYYY-MM-DD
  billing_start: string; // Date string YYYY-MM-DD
}

export interface UpdateSubscriptionRequest {
  plan_name?: SubscriptionPlan;
  status?: SubscriptionStatus;
  minutes_total?: number;
  billing_cycle?: BillingCycle;
  price_per_minute?: string;
  minutes_reset_start?: string; // Date string YYYY-MM-DD
  billing_start?: string; // Date string YYYY-MM-DD
  is_deleted?: boolean;
}

// Plan pricing configuration (matches backend)
export const PLAN_PRICING: Record<SubscriptionPlan, { monthly: string; yearly: string }> = {
  Starter: { monthly: "0.59", yearly: "0.47" },
  "Starter Pro": { monthly: "0.49", yearly: "0.39" },
  Clinic: { monthly: "0.39", yearly: "0.31" },
  "Clinic Pro": { monthly: "0.29", yearly: "0.23" },
  Enterprise: { monthly: "0.00", yearly: "0.00" }, // Custom pricing
};

export const PLAN_MINUTES: Record<SubscriptionPlan, number | null> = {
  Starter: 500,
  "Starter Pro": 1000,
  Clinic: 2000,
  "Clinic Pro": 5000,
  Enterprise: null, // Custom
};

// User types
export interface CognitoUser {
  username: string;
  email: string;
  email_verified: boolean;
  enabled: boolean;
  user_status: string;
  given_name: string | null;
  family_name: string | null;
  terms_version: string | null;
  registration_ip: string | null;
  registration_date: string | null;
  groups: string[];
  client_id: string | null;
  user_create_date: string;
  user_last_modified_date: string;
}

export interface UpdateUserRequest {
  email?: string;
  given_name?: string;
  family_name?: string;
  enabled?: boolean;
  // Include to change the user's client assignment; pass null to unassign.
  client_id?: string | null;
}

export interface UpdateUserGroupsRequest {
  groups: string[];
}

// Service types
export type PriceType = "fixed" | "from" | "none";
export type GenderRestriction = "male" | "female" | "none";

export interface ServiceRestrictions {
  minimum_age: number | null;
  maximum_age: number | null;
  gender_restriction: GenderRestriction | null;
  warning_message: string | null;
}

export interface Service {
  id: string;
  /** The id the agent's knowledge base and tool calls use (`svc_662f757f`); searchable. */
  short_ref?: string;
  client_id: string;
  name: string;
  short_summary: string | null;
  faqs: string | null;
  post_appointment_notice: string | null;
  duration: number;
  /** The PMS-synced price. For a `syncsPrice` vendor this is the VENDOR's value. */
  price_type: PriceType;
  price: string | null;
  /**
   * The clinic's override, which beats the synced price permanently. A non-null
   * `price_override_type` — not the amount — means an override is in force, so
   * that "override to quote no price" stays distinguishable from "no override".
   */
  price_override: string | null;
  price_override_type: PriceType | null;
  /** What the agent actually quotes: the override if there is one, else the synced pair. */
  effective_price: string | null;
  effective_price_type: PriceType;
  restrictions: ServiceRestrictions | null;
  is_private_available: boolean;
  is_insurance_available: boolean;
  is_public_health_available: boolean;
  agent_available: boolean;
  // false = the agent knows the service and can describe it, but must not
  // search availability or book it; the clinic arranges it directly.
  agent_bookable: boolean;
  // false = patients booked for this service are neither reminded nor asked to
  // confirm, on any channel. Clinic-owned; PMS syncs never touch it.
  appointment_reminders_enabled: boolean;
  // true = the agent books a returning patient with the professional who last
  // treated them for this service, unless the caller asks for someone else.
  prefer_previous_professional: boolean;
  // Minutes kept blocked immediately after an appointment for this service ends,
  // so nothing can be booked right up against it. 0 = no buffer (default).
  post_appointment_buffer_minutes: number;
  is_deleted: boolean;
  sort_order: number;
  /** `duration_override` is null when this professional uses the service's own `duration`. */
  professionals: Array<{ id: string; short_ref?: string; name: string; duration_override: number | null }>;
  /**
   * Minutes for the payment types whose duration differs from `duration`. Wins over a
   * professional's `duration_override`. Absent type = the professional's or the service's.
   */
  payment_type_durations?: Partial<Record<BookingPaymentType, number>>;
  /** Services interchangeable with this one: the agent's availability search covers them too. */
  equivalent_service_ids?: string[];
  created_at: string;
  updated_at: string;
  source: string;
  external_id: string | null;
  /**
   * What the source PMS currently calls this service. Refreshed on every sync, while
   * `name` is only ever written on insert — so a `name` that differs means the clinic
   * renamed it, and this is what to revert to. Null for a manual service, a variant or
   * a composite.
   */
  external_name?: string | null;
  /**
   * Set on a variant: a local service the agent books in place of the PMS service it
   * was split from. Duration, price, payment flags, reminders and buffer are the
   * parent's (the backend copies them in); the PMS receives the parent.
   */
  split_from_service_id: string | null;
  /**
   * Set on a composite visit: one service the agent books as its parts, in order, the
   * same day. A composite has no professionals of its own; its `duration` is the typical
   * visit length and its payment flags are the types every part accepts (both derived).
   */
  composition: ServiceComposition | null;
}

/**
 * How a composite's parts are timed. Sequential: each part starts min..max minutes after the
 * previous one ends. Parallel: the parts overlap, and each part but the main one starts
 * min..max minutes after the main part starts.
 */
export type CompositeArrangement = "sequential" | "parallel";

export interface ServiceComposition {
  /** In booking order. */
  part_service_ids: string[];
  /** Sequential: the wait after the previous part. Parallel: the start delay after the main part starts. */
  min_gap_minutes: number;
  max_gap_minutes: number;
  /** 0-based index into the parts: the part whose professional the agent names, unless `named_part_position` says otherwise. In a parallel visit it is also the anchor that sets the visit's start and length. */
  main_part_position: number;
  /** The part whose professional the agent names when it is not the main part; null = the main part. */
  named_part_position?: number | null;
  arrangement: CompositeArrangement;
}

export interface CreateServiceRequest {
  name: string;
  duration: number;
  short_summary?: string;
  faqs?: string;
  post_appointment_notice?: string;
  price_type?: PriceType;
  price?: number;
  restrictions?: Partial<ServiceRestrictions>;
  is_private_available?: boolean;
  is_insurance_available?: boolean;
  is_public_health_available?: boolean;
  agent_available?: boolean;
  agent_bookable?: boolean;
  appointment_reminders_enabled?: boolean;
  prefer_previous_professional?: boolean;
  post_appointment_buffer_minutes?: number;
  sort_order?: number;
}

export interface UpdateServiceRequest {
  name?: string;
  duration?: number;
  short_summary?: string | null;
  faqs?: string | null;
  post_appointment_notice?: string | null;
  price_type?: PriceType;
  price?: number | null;
  /**
   * Send BOTH together (the backend 422s otherwise), or both as null to remove
   * the override and hand the price back to the PMS.
   */
  price_override?: number | null;
  price_override_type?: PriceType | null;
  restrictions?: Partial<ServiceRestrictions> | null;
  is_private_available?: boolean;
  is_insurance_available?: boolean;
  is_public_health_available?: boolean;
  agent_available?: boolean;
  agent_bookable?: boolean;
  appointment_reminders_enabled?: boolean;
  prefer_previous_professional?: boolean;
  post_appointment_buffer_minutes?: number;
  sort_order?: number;
  professional_ids?: string[];
  /** Replaces the whole set of equivalent services (symmetric); [] removes them all. */
  equivalent_service_ids?: string[];
  /**
   * Per-professional appointment durations for this service: professional id →
   * minutes, or null to clear one. Only the entries sent are touched.
   */
  professional_durations?: Record<string, number | null>;
  /** Replaces the whole map of per-payment-type durations; {} removes them all. */
  payment_type_durations?: Partial<Record<BookingPaymentType, number>>;
}

/** A variant takes only what tells it apart; everything else is inherited from the parent. */
export interface CreateServiceVariantRequest {
  name: string;
  short_summary?: string | null;
  faqs?: string | null;
  post_appointment_notice?: string | null;
  /** At least one, all of them professionals of the parent service. */
  professional_ids: string[];
}

export interface UpdateServiceCompositionRequest {
  /** 2 to 4 PMS services or variants, in booking order. */
  part_service_ids: string[];
  min_gap_minutes: number;
  max_gap_minutes: number;
  main_part_position: number | null;
  named_part_position: number | null;
  arrangement: CompositeArrangement;
}

/** Duration, professionals and payment types are not sent: they come from the parts. */
export interface CreateCompositeServiceRequest extends UpdateServiceCompositionRequest {
  name: string;
  short_summary?: string | null;
  faqs?: string | null;
  post_appointment_notice?: string | null;
  price_type?: PriceType;
  price?: number | null;
  agent_bookable?: boolean;
}

// Call types
export type CallHandlingStatus =
  | "agent_handled"
  | "clinic_handled"
  | "auto_message"
  | "voicemail"
  | "unknown";

export type QualityVerdict = "pass" | "review" | "fail";

export interface QualityRuleEvidence {
  rule: string;
  penalty: number;
  evidence: Record<string, unknown>;
}

/** `calls.channel` — the transport a conversation arrived on. */
export type CallChannel = "phone" | "whatsapp" | "unknown";

/** A staff member's internal note on a conversation, as the list endpoint summarises it. */
export interface InternalNote {
  text: string;
  author_name: string | null;
  created_at: string;
}

export interface Call {
  id: string;
  agent_id: string;
  conversation_id: string;
  agent_mode: "off" | "shadow" | "live";
  type: string | null;
  status: string | null;
  called_at: string;
  call_duration_secs: number | null;
  caller_number: string | null;
  caller_name?: string | null;
  agent_number: string | null;
  direction: string | null;
  transcript_summary: string | null;
  call_successful: string | null;
  call_summary_title: string | null;
  /**
   * Names WHICH canned summary the backend composed, when it composed one itself
   * ("missed_call_no_speech", "brief_call"). null when `transcript_summary` holds a real
   * model-written summary, and on every row predating the column. Resolve it through
   * `callSummaryKey` rather than reading it directly — older rows carry no key and are
   * recognised by `type`/prose instead.
   */
  summary_key: string | null;
  audio_s3_key: string | null;
  audio_url: string | null; // Presigned S3 URL
  transcription_s3_key: string | null;
  source: string | null;
  /**
   * Transport the conversation arrived on. "phone" for telephony, "whatsapp" for a
   * patient messaging the clinic; "unknown" only on rows that predate the column.
   *
   * A WhatsApp conversation has no audio and no meaningful duration, so the phone-only
   * affordances (recording player, tel: callback, duration) key off this.
   */
  channel: CallChannel;
  /**
   * WhatsApp only: the last message written in the conversation, for the list's preview.
   * `sender` is the patient or the agent (transcripts carry no clinic-staff role). Null on
   * a phone call, and absent until the API version that sends it is deployed.
   */
  last_message?: { text: string; sender: "patient" | "agent" } | null;
  /** Internal notes staff left on the conversation, newest first. Absent until the API
   * version that sends it is deployed, and empty when there are none. */
  internal_notes?: InternalNote[];
  /**
   * Which of the clinic's deployed agents served it ("working-hours" | "off-hours" |
   * "whatsapp-router"). Never "clinic-manager" in a list response — a staff member's
   * conversation with the privileged agent is excluded from /calls entirely — so this is
   * only ever seen on a direct link.
   */
  agent_role?: string | null;
  /**
   * Set only when a transfer moved the conversation: `agent_id` then names the agent it
   * started on (the WhatsApp router) and this names the one that actually served the caller.
   */
  handled_by_agent_id?: string | null;
  processing_status?: "pending" | "processing" | "completed" | "failed";
  call_handling_status: CallHandlingStatus;
  quality_score: number | null;
  quality_verdict: QualityVerdict | null;
  quality_rules: QualityRuleEvidence[] | null;
  quality_rule_version: string | null;
  quality_evaluated_at: string | null;
  agent_version: string | null;
  elevenlabs_agent_version_id?: string | null;
  /**
   * Which ElevenLabs workflow node conducted the call = the per-call tool profile the
   * caller actually got ("default" | "known_caller" | "test" | "known_caller_test").
   * Null for clinics whose agents have no workflow, and for calls predating the column.
   */
  workflow_node_id?: string | null;
  // Derived from the call's associated booking (services/professionals join).
  // Optional: populated by the calls-list endpoint once the backend exposes it.
  service_name?: string | null;
  professional_name?: string | null;
  /**
   * True only for a genuine single-channel voicemail recording -- no agent audio
   * at all, just the caller's voice. Distinct from `type === "missed_call"`, which
   * the backend also assigns to answered calls with real agent audio whose caller
   * simply never said anything substantive.
   */
  is_voicemail?: boolean;
  created_at: string;
  updated_at: string;
}

/** Conversation-scoped lists `GET /calls/{id}` can return alongside the call. */
export type CallDetailInclude = "activity_log" | "agent_tasks" | "bookings" | "team_tasks";

/** A call plus the lists requested through `include`; a list not requested is `null`. */
export interface CallDetail extends Call {
  activity_log?: ActivityLogEntry[] | null;
  agent_tasks?: AgentTask[] | null;
  bookings?: BookingResponse[] | null;
  team_tasks?: AgentTeamTask[] | null;
}

export interface CallExtraData {
  id: string;
  cif: string | null;
  company_name: string | null;
}


/**
 * Filter options available for the queried set — computed by the backend over
 * the date range + search + active tab, ignoring the four facet filters between
 * themselves so a filter never narrows itself. Lists arrive sorted, without
 * nulls or duplicates. Optional: absent until the backend ships them.
 */
export interface CallFacets {
  handled_by?: string[];
  type?: string[];
  /** Channels present in the queried set — "phone", "whatsapp", or both. */
  channel?: string[];
  /**
   * The `<channel>_<direction>` pairs present in the queried set. What the channel
   * selector is built from: it offers the four kinds as one dropdown, so its values are
   * pairs. Absent from an API deployed before the selector shipped, which is why the
   * selector falls back to its own catalog.
   */
  channel_direction?: string[];
  quality_verdict?: string[];
  fired_rule?: string[];
}

export interface PaginatedCallsResponse {
  items: Call[];
  total: number;
  skip: number;
  limit: number;
  /** null when the request sent `include_summary=false`. */
  needs_attention_count: number | null;
  /** Selectable filter values for the queried set (not just this page). null when the
   *  request did not ask for them (`include_facets`). */
  facets?: CallFacets | null;
}

export interface CallOwner {
  client_id: string;
  client_name: string;
}

/** One feature flag's state for a specific call (admin-only). */
export interface CallFeatureFlag {
  key: string;
  /** "agent-build" | "backend-runtime" — determines how `state` was obtained. */
  evaluated_at: string;
  /**
   * "on" | "off" | "unknown". `unknown` is NOT `off`: it means the value could not be
   * determined for this call, so the UI must render it distinctly.
   */
  state: string;
  /** agent_config_snapshot | recorded_at_call_start | not_recorded | deployment_not_found */
  source: string;
  /** The evidence the state was read from, e.g. `turn_eagerness=patient`. */
  detail: string;
}

/**
 * Which feature flags a given call ran with. Admin-only.
 *
 * Not a live evaluation: agent-build flags are derived from the agent config that was
 * actually deployed when the call happened, and backend-runtime flags come from what was
 * recorded at call start. For a past call, today's flag values can differ.
 */
export interface CallFeatureFlags {
  call_id: string;
  agent_version: string | null;
  /** Which of the client's agents answered: "working-hours" | "off-hours" |
   *  "whatsapp-router" | "clinic-manager". */
  agent_type: string | null;
  deployed_at: string | null;
  flags: CallFeatureFlag[];
}

/** A service the agent named by id, as the clinic's catalogue names it. */
export interface ResolvedToolService {
  ref: string;
  name: string;
  duration_minutes: number;
}

export interface ResolvedToolProfessional {
  ref: string;
  name: string;
}

/** What the ids in an invocation's request stand for; null when nothing matched. */
export interface ResolvedToolRefs {
  service: ResolvedToolService | null;
  professional: ResolvedToolProfessional | null;
  excluded_professionals: ResolvedToolProfessional[];
}

/**
 * One tool the agent called during a conversation, as our API recorded it.
 * Bodies are stored PHI-scrubbed: names and phones appear as NAME_… / PHONE_… tokens.
 */
export interface CallToolInvocation {
  id: string;
  conversation_id: string | null;
  tool_name: string;
  endpoint_path: string;
  status_code: number;
  error_detail: string | null;
  duration_ms: number;
  created_at: string;
  /** Seconds from the call's start to the tool call — the transcript turns' clock. */
  time_in_call_secs: number;
  request_params: Record<string, unknown> | unknown[] | null;
  response_body: Record<string, unknown> | unknown[] | null;
  resolved: ResolvedToolRefs;
}

/** Admin-only: the tool timeline of one call, oldest first. */
export interface CallToolInvocations {
  call_id: string;
  conversation_id: string;
  invocations: CallToolInvocation[];
}

/** Uniform paginated list envelope returned by every list endpoint. */
export interface Paginated<T> {
  items: T[];
  total: number;
  skip: number;
  limit: number;
}

export interface ListServicesParams {
  search?: string;
  skip?: number;
  limit?: number;
  orderBy?: string;
  orderDir?: 'asc' | 'desc';
  includeDeleted?: boolean;
}

export interface ListProfessionalsParams {
  search?: string;
  skip?: number;
  limit?: number;
  orderBy?: string;
  orderDir?: 'asc' | 'desc';
  includeDeleted?: boolean;
}

export interface ListClientsParams {
  search?: string;
  skip?: number;
  limit?: number;
  includeDeleted?: boolean;
}

export interface ListInvitationsParams {
  search?: string;
  status?: string;
  skip?: number;
  limit?: number;
}

export interface ListUsersParams {
  search?: string;
  skip?: number;
  limit?: number;
}

export interface ListCallsParams {
  agent_id?: string;
  status?: string;
  type?: string;
  start_date?: string;
  end_date?: string;
  handled_by?: CallHandlingStatus;
  /** Narrows to one transport. Never widens — an outbound WhatsApp notification is not a
   *  conversation and stays hidden either way. */
  channel?: CallChannel;
  /** Who opened the conversation. Pairs with `channel`; see CHANNEL_VALUES. */
  direction?: string;
  quality_verdict?: QualityVerdict[];
  min_score?: number;
  max_score?: number;
  fired_rule?: string[];
  search?: string;
  search_in_summary?: boolean;
  search_in_name?: boolean;
  /** Only the callers who could not talk to the clinic: one row each, their latest call in
   *  the window that reached nobody, unless they got through later. */
  unreached?: boolean;
  order_by?: string;
  order_dir?: 'asc' | 'desc';
  skip?: number;
  limit?: number;
  /** A `/conversations` tab: the backend applies that tab's rules to the filters (calls
   *  forces the phone channel; whatsapp forces its channel and drops `handled_by`). */
  view?: ConversationView;
  /** `false` returns `needs_attention_count` as null (the `/conversations` banner reads
   *  it from `GET /calls/summary`). Omitted, the backend computes it. `total` is always
   *  computed. */
  include_summary?: boolean;
  /** Whether to compute `facets`; `false` returns them as null — for paging or sorting,
   *  which cannot change them. Omitted, it follows `include_summary`. */
  include_facets?: boolean;
}

/** Which conversations `/conversations` lists: everything, phone calls only, or WhatsApp only. */
export type ConversationView = "all" | "calls" | "whatsapp";

/** The filters the All tab's list sends; `GET /calls/summary` applies each tab's rules. */
export type CallsSummaryParams = Pick<
  ListCallsParams,
  | "start_date"
  | "end_date"
  | "status"
  | "type"
  | "handled_by"
  | "channel"
  | "direction"
  | "search"
  | "search_in_summary"
  | "search_in_name"
  | "unreached"
>;

export interface CallsSummaryResponse {
  /** Each equals the `total` `GET /calls` returns for that tab under the same filters. */
  view_counts: Record<ConversationView, number>;
  /** The client's whole history, whatever the filters — as on `GET /calls`. */
  needs_attention_count: number;
}

// Transcription types
export interface TranscriptionWord {
  text: string;
  start: number;
  end: number;
  type: string;
  speaker: string | null;
  confidence: number | null;
}

export interface TranscriptionTurn {
  speaker: string;
  start: number;
  end: number;
  text: string;
  // Whether the recording has sound for this turn. A WhatsApp thread mixes spoken voice
  // notes with typed messages, and the agent never speaks there — so playback is offered
  // only where this is not false. Absent on transcripts stored before the backend recorded
  // it, which must keep their buttons: every turn of a phone call does have audio.
  has_audio?: boolean | null;
}

export interface TranscriptionResponse {
  text: string;
  language_code: string | null;
  language_probability: number | null;
  words: TranscriptionWord[];
  turns: TranscriptionTurn[];
}

// Professional types
export type ProfessionalGender = "male" | "female";

export interface Professional {
  id: string; // UUID
  /** The id the agent's knowledge base and tool calls use (`prof_662f757f`); searchable. */
  short_ref?: string;
  client_id: string;
  name: string;
  specializations: string | null;
  // Inferred from the name when left empty; used by the agent for pronouns
  gender: ProfessionalGender | null;
  service_ids?: string[]; // Legacy field, may not be present
  services: Array<{ id: string; short_ref?: string; name: string; is_deleted: boolean }>; // New field with full service objects
  agent_available: boolean;
  is_deleted: boolean;
  sort_order: number;
  source: string; // 'internal' or 'archivex'
  external_id: string | null; // External system ID (e.g., Archivex ID)
  // What the source integration currently calls this professional. Refreshed on
  // every sync, while `name` is only ever written on insert — so `name !==
  // external_name` means the clinic renamed it, and this is what to revert to.
  // Null for a manual professional, and for one not re-synced since V208.
  external_name: string | null;
  // Who owns the weekly agenda hours. "toniagent" is only ever set on a Gesden
  // professional, by staff; its PMS hours then stop applying. Optional only so a
  // panel deployed ahead of the API reads a missing value as "sync".
  schedule_managed_by?: ScheduleOwner;
  created_at: string;
  updated_at: string;
}

export type ScheduleOwner = "sync" | "toniagent";

export interface CreateProfessionalRequest {
  name: string;
  specializations?: string;
  service_ids: string[];
  agent_available?: boolean;
}

export interface UpdateProfessionalRequest {
  name?: string;
  specializations?: string;
  service_ids?: string[];
  sort_order?: number;
  agent_available?: boolean;
  // null clears it, which lets the backend infer it from the name again
  gender?: ProfessionalGender | null;
}

// Insurance types
export interface Insurance {
  id: string;
  /** The id the agent's knowledge base and tool calls use (`ins_662f757f`); searchable. */
  short_ref?: string;
  client_id: string;
  source: string;
  external_id: string | null;
  name: string;
  details: string | null;
  is_private_payer: boolean;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateInsuranceRequest {
  name: string;
  details?: string | null;
  is_private_payer?: boolean;
}

export interface UpdateInsuranceRequest {
  name?: string;
  details?: string | null;
  is_private_payer?: boolean;
}

// Business hours exception types.
// "modified" only ever exists as legacy data (see .claude/rules — the type was never
// wired into slot search or the agent KB and was removed from creation); the response
// type stays permissive so reading an old row doesn't break, but new/updated
// exceptions are always "closed".
export interface BusinessHoursException {
  id: string;
  client_id: string;
  exception_type: "closed" | "modified";
  start_date: string;
  end_date: string | null;
  start_time: string | null;
  end_time: string | null;
  break_start_time: string | null;
  break_end_time: string | null;
  reason: string | null;
  created_at: string;
  updated_at: string;
  /** See AgendaException.managed_by — same rule, clinic-wide closures. */
  managed_by?: string | null;
}

export interface BusinessHoursExceptionRequest {
  exception_type: "closed";
  start_date: string;
  end_date?: string | null;
  reason?: string | null;
}

/** A number the clinic transfers straight to its staff; the agent never answers it. */
export interface TransferCaller {
  id: string;
  client_id: string;
  phone_number: string;
  label: string | null;
  created_at: string;
}

// Agenda types
export interface Agenda {
  id: string; // UUID
  client_id: string;
  professional_id: string; // UUID
  professional_name: string; // Joined from professionals table
  location_id: string | null; // Prefixed "loc_<uuid>"; always populated post-V172 backfill
  slot_duration: number;
  calendar_integration_id: string | null; // UUID of linked CalendarIntegration
  // Weekly schedule (null = day is off)
  monday_start: string | null; // "HH:MM"
  monday_end: string | null;
  tuesday_start: string | null;
  tuesday_end: string | null;
  wednesday_start: string | null;
  wednesday_end: string | null;
  thursday_start: string | null;
  thursday_end: string | null;
  friday_start: string | null;
  friday_end: string | null;
  saturday_start: string | null;
  saturday_end: string | null;
  sunday_start: string | null;
  sunday_end: string | null;
  // One optional break per working day ("HH:MM"), strictly inside its hours.
  // Optional only for the deploy window, while an older API still omits it.
  monday_break_start?: string | null;
  monday_break_end?: string | null;
  tuesday_break_start?: string | null;
  tuesday_break_end?: string | null;
  wednesday_break_start?: string | null;
  wednesday_break_end?: string | null;
  thursday_break_start?: string | null;
  thursday_break_end?: string | null;
  friday_break_start?: string | null;
  friday_break_end?: string | null;
  saturday_break_start?: string | null;
  saturday_break_end?: string | null;
  sunday_break_start?: string | null;
  sunday_break_end?: string | null;
  is_deleted: boolean;
  /** Ownership: 'manual' or an integration key (e.g. 'docfav', 'clinic_cloud'). Optional until the backend exposing it is deployed. */
  source?: string;
  /** Raw external-system agenda id (e.g. the ClinicCloud idAgenda). Null for manual agendas. */
  external_id?: string | null;
  /** Human-readable external agenda label (e.g. "(5)GIRONA - DR.ARASA"); disambiguates a pro's per-site agendas. */
  external_name?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateAgendaRequest {
  professional_id: string; // UUID
  location_id?: string | null; // Multi-location only; backend defaults to the primary location when omitted
  slot_duration: number;
  // Weekly schedule
  monday_start?: string | null;
  monday_end?: string | null;
  tuesday_start?: string | null;
  tuesday_end?: string | null;
  wednesday_start?: string | null;
  wednesday_end?: string | null;
  thursday_start?: string | null;
  thursday_end?: string | null;
  friday_start?: string | null;
  friday_end?: string | null;
  saturday_start?: string | null;
  saturday_end?: string | null;
  sunday_start?: string | null;
  sunday_end?: string | null;
  monday_break_start?: string | null;
  monday_break_end?: string | null;
  tuesday_break_start?: string | null;
  tuesday_break_end?: string | null;
  wednesday_break_start?: string | null;
  wednesday_break_end?: string | null;
  thursday_break_start?: string | null;
  thursday_break_end?: string | null;
  friday_break_start?: string | null;
  friday_break_end?: string | null;
  saturday_break_start?: string | null;
  saturday_break_end?: string | null;
  sunday_break_start?: string | null;
  sunday_break_end?: string | null;
}

export interface UpdateAgendaRequest {
  slot_duration?: number;
  calendar_integration_id?: string | null;
  // Weekly schedule
  monday_start?: string | null;
  monday_end?: string | null;
  tuesday_start?: string | null;
  tuesday_end?: string | null;
  wednesday_start?: string | null;
  wednesday_end?: string | null;
  thursday_start?: string | null;
  thursday_end?: string | null;
  friday_start?: string | null;
  friday_end?: string | null;
  saturday_start?: string | null;
  saturday_end?: string | null;
  sunday_start?: string | null;
  sunday_end?: string | null;
  monday_break_start?: string | null;
  monday_break_end?: string | null;
  tuesday_break_start?: string | null;
  tuesday_break_end?: string | null;
  wednesday_break_start?: string | null;
  wednesday_break_end?: string | null;
  thursday_break_start?: string | null;
  thursday_break_end?: string | null;
  friday_break_start?: string | null;
  friday_break_end?: string | null;
  saturday_break_start?: string | null;
  saturday_break_end?: string | null;
  sunday_break_start?: string | null;
  sunday_break_end?: string | null;
  is_deleted?: boolean;
}

/** One agenda's resolved open/close/breaks on one date — GET /agendas/day-schedule.
 * A professional with no agenda gets one closed entry (agenda_id null); a
 * professional with multiple agendas (e.g. split across locations) gets one
 * entry per agenda. Powers the calendar day-view's availability shading.
 * `agenda_id`/`location_id` are also null for a mediconta/serin_iom
 * professional resolved live from availability slots + bookings (no
 * `agendas` row backing that entry at all). */
export interface AgendaDaySchedule {
  professional_id: string;
  agenda_id: string | null;
  location_id: string | null;
  /** "YYYY-MM-DD" — a range request (dateTo) returns one entry per date. */
  date: string;
  closed: boolean;
  open: string | null; // "HH:MM"
  close: string | null; // "HH:MM"
  breaks: { start: string; end: string }[]; // "HH:MM"
  /** True when open/close come from an exception overriding this date's hours
   * rather than the professional's normal weekly agenda ("modified working
   * hours") — never true for a closed day. */
  is_modified: boolean;
  modified_reason: string | null;
  /** The vacation/time-off exception covering this professional on this date,
   * if any — resolved by the backend so the calendar renders the band without
   * fetching every agenda's exceptions itself. */
  vacation: AgendaException | null;
  /** This agenda's booking rules in force on this date — already filtered to
   * the date's weekday (and to non-deleted rows) by the backend. */
  slot_rules: AgendaSlotRule[];
  /** PMS-side schedule blocks ("not bookable online") — merged, clipped to working
   * hours, `[]` on a closed day. Optional only for the deploy window, while an
   * older API still omits it. */
  blocked?: { start: string; end: string }[]; // "HH:MM"
}

// Agenda Exception types
export interface AgendaException {
  id: string; // UUID
  client_id: string;
  agenda_id: string | null; // UUID
  professional_id: string | null; // UUID
  date: string | null; // ISO date
  start_date_time: string | null; // ISO datetime
  end_date_time: string | null; // ISO datetime
  is_available: boolean;
  start_time_override: string | null; // "HH:MM"
  end_time_override: string | null; // "HH:MM"
  is_recurring: boolean;
  recurrence_rule: string | null; // RRULE format
  reason: string | null;
  created_by: string;
  created_at: string;
  /**
   * The integration whose sync owns this row, or null when a person created it.
   * Sync-owned rows are read-only here: the next sync recreates whatever we delete.
   * Optional because the two repos deploy independently — absent means "not yet
   * served by the API", which reads the same as unowned.
   */
  managed_by?: string | null;
}

export interface CreateAgendaExceptionRequest {
  agenda_id?: string | null;
  professional_id?: string | null;
  date?: string | null;
  start_date_time?: string | null;
  end_date_time?: string | null;
  is_available: boolean;
  start_time_override?: string | null;
  end_time_override?: string | null;
  is_recurring?: boolean;
  recurrence_rule?: string | null;
  reason?: string | null;
  created_by: string;
}

// Agenda Slot Rule types
export interface AgendaSlotRule {
  id: string; // UUID
  agenda_id: string; // UUID
  name: string;
  day_of_week: number | null; // 0=Monday, 6=Sunday, null=all days
  start_time: string; // "HH:MM"
  end_time: string; // "HH:MM"
  allowed_payment_types: string[]; // ['private', 'insurance', 'public_health']
  allowed_service_ids: string[]; // UUID[]
  release_hours_before: number | null;
  // Max bookings of a covered service that may start inside this window, per
  // professional per day. Set only on a cap. Enforced on the agent path only.
  max_bookings: number | null;
  // Which kind of rule this is. The two read `allowed_service_ids` differently:
  // 'restriction' treats it as an ALLOWLIST (other services are rejected in the
  // window), 'cap' treats it as a SCOPE FILTER for the max_bookings quota (other
  // services are untouched). Optional because the field can be absent mid-rollout,
  // the two repos deploying independently.
  rule_type?: AgendaSlotRuleType;
  // A DATED reserved window (company#2874): applies only on the RRULE's occurrences from
  // `starts_on` through `ends_on`, and opens its hours there even on a day off. Null on a
  // weekly rule. Optional while the API rolls out.
  recurrence_rule?: string | null;
  starts_on?: string | null; // YYYY-MM-DD
  ends_on?: string | null; // YYYY-MM-DD, inclusive; null = no end
  // How long an appointment of a service this restriction admits lasts inside the window.
  slot_duration_minutes?: number | null;
  is_deleted: boolean;
  // 'manual' = created in the panel; anything else is written by that
  // integration's sync and must stay read-only in the UI.
  source: string;
  created_at: string;
  updated_at: string;
}

// Keep in sync with `RuleType` in control-panel-api/app/schemas/agenda_slot_rule.py.
export type AgendaSlotRuleType = "restriction" | "cap";

export interface CreateAgendaSlotRuleRequest {
  agenda_id: string;
  name: string;
  day_of_week?: number | null;
  start_time: string;
  end_time: string;
  allowed_payment_types?: string[];
  allowed_service_ids?: string[];
  release_hours_before?: number | null;
  max_bookings?: number | null;
  rule_type?: AgendaSlotRuleType;
  recurrence_rule?: string | null;
  starts_on?: string | null;
  ends_on?: string | null;
  slot_duration_minutes?: number | null;
}

export interface UpdateAgendaSlotRuleRequest {
  name?: string;
  day_of_week?: number | null;
  start_time?: string;
  end_time?: string;
  allowed_payment_types?: string[];
  allowed_service_ids?: string[];
  release_hours_before?: number | null;
  max_bookings?: number | null;
  rule_type?: AgendaSlotRuleType;
  recurrence_rule?: string | null;
  starts_on?: string | null;
  ends_on?: string | null;
  slot_duration_minutes?: number | null;
  is_deleted?: boolean;
}

// Agenda Day-Slot-Duration Override types
/** Overrides `Agenda.slot_duration` (candidate-slot cadence, not appointment
 * length) for one day of week. Absence for a given day means "use the
 * agenda's default `slot_duration`". */
export interface AgendaDaySlotDuration {
  id: string; // UUID
  agenda_id: string; // UUID
  day_of_week: number; // 0=Monday, 6=Sunday
  slot_duration: number; // minutes
  created_at: string;
  updated_at: string;
}

export interface UpsertAgendaDaySlotDurationRequest {
  slot_duration: number;
}

// Agent Task types
export interface AgentTask {
  id: string; // UUID
  client_id: string;
  conversation_id: string | null;
  task_type: string;
  title: string;
  description: string | null;
  status: string; // 'pending', 'in_progress', 'completed', 'dismissed'
  priority: string; // 'low', 'medium', 'high', 'urgent'
  assigned_to: string | null; // cognito_user_id
  due_date: string | null; // ISO timestamp
  patient_phone: string | null;
  patient_name: string | null;
  completed_at: string | null;
  completed_by: string | null;
  notes: string | null;
  i18n_key: string | null; // e.g. "tasks.auto_review.cancellation.manual"; null for tasks without localized copy
  created_at: string;
  updated_at: string;
}

/**
 * A ToniAgent-team-owned task (`agent_feedback` / `knowledge_gap`), as served by
 * the admin-only queue endpoints. These are excluded from every clinic-facing
 * task response, so this is the only shape that carries them.
 */
export interface AgentTeamTask {
  id: string;
  client_id: string;
  client_name: string;
  conversation_id: string;
  /** Prefixed call id for deep-linking to /calls/<id>; null if the call row is missing. */
  call_id: string | null;
  task_type: string;
  title: string;
  description: string | null;
  status: string;
  notes: string | null;
  i18n_key: string | null;
  /** "agent" when the voice agent or the KB-gap detector filed it, "staff" when a human did. */
  origin: "agent" | "staff";
  completed_at: string | null;
  completed_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface AgentTaskCreate {
  conversation_id?: string;
  task_type: string;
  title: string;
  description?: string;
  status?: string;
  priority?: string;
  assigned_to?: string;
  due_date?: string;
  patient_phone?: string;
  patient_name?: string;
  notes?: string;
  completed_by?: string;
}

export interface BookingResponse {
  id: string;
  professional_id: string;
  /** Nullable: null for source="external_sync" rows the backend couldn't resolve a service for. */
  service_id: string | null;
  /** Nullable: null for a PMS-mirror row (e.g. source="external_sync") the backend
   * couldn't resolve to a local patient. Needed by AddToWaitlistDrawer's appointment
   * picker (the waiting list always starts from an existing booking's patient). */
  patient_id: string | null;
  /** Multi-location only; null for single-location clients. */
  location_id: string | null;
  booked_for_name: string | null;
  booked_for_phone: string | null;
  start_date_time: string;
  end_date_time: string;
  status: string;
  /** toniagent_voice / toniagent_whatsapp (agent) · staff (manual) · external_sync · patient_web */
  source: string;
  payment_type: string;
  notes: string | null;
  conversation_id: string | null;
  created_at: string;
  /**
   * For a native (staff/agent-created) booking: only populated when listing
   * by conversation_id — the date-filtered calendar query (listBookings({
   * date })) has no join for these, so resolve the display name from the
   * separately-fetched professionals/services lists instead.
   *
   * For a PMS-mirror booking (e.g. Clinic Cloud, which has no service id on
   * an appointment at all — only this free-text vendor label), the
   * date-filtered query DOES populate it: use it as the fallback once
   * service_id-based lookup comes back empty, before falling back further to
   * an "Unknown service" placeholder. See AgendasCalendarView's serviceName.
   */
  service_name: string | null;
  professional_name: string | null;
  /**
   * True for a Google Calendar entry that isn't a real timed appointment —
   * an all-day event, or one spanning more than a day (a clinic block:
   * vacation, closure). Always false for a native/PMS-mirror booking, which
   * is never all-day. Render a date range instead of a per-slot duration
   * when true — see AppointmentDetailDrawer/ApptBlock.
   */
  is_all_day: boolean;
  /**
   * All three are overlaid server-side by AppointmentStateResolver, NOT read off the
   * booking row: `bookings.confirmed_at` / `reminder_sent_at` exist only on the native
   * table and every PMS mirror projects them NULL, and `declined_at` is not a column at
   * all — the patient's answer lives in `appointment_confirmations`.
   *
   * `declined_at` is deliberately not folded into `confirmed_at`: a patient who answered
   * "I cannot come" and one who never answered BOTH leave `confirmed_at` null, and the
   * drawer showed the two identically as "waiting for the patient" until this existed.
   */
  confirmed_at: string | null;
  declined_at: string | null;
  reminder_sent_at: string | null;
  /**
   * When the morning sweep will first send this appointment's reminder, or null when it
   * never will. Only the calendar listing (`GET /bookings?date=`) fills it, from the
   * sweep's own rules — the drawer shows it rather than computing a date.
   */
  reminder_scheduled_for?: string | null;
  /**
   * Why the sweep will NOT send one, in its own vocabulary (`too_soon`, `just_booked`,
   * `landline`, `do_not_contact`, `service_reminders_disabled`, …). Calendar listing only.
   */
  reminder_skip_reason?: string | null;
  /** Set on the calendar listing when this appointment is one part of a composite visit. */
  composite_visit?: CompositeVisitMarker | null;
  /**
   * Whether `rescheduleBooking` accepts this appointment — the API's own rule, so the
   * calendar never offers a move the endpoint refuses (a Docfav row once answered 422
   * "Invalid booking_id"). Optional: absent from an API that predates it, read as false.
   */
  can_reschedule?: boolean;
}

/**
 * Cancelling one part of a composite visit cancels the whole visit, unless the request
 * asked for `only_this_part`. The other parts' ids are listed only then;
 * `other_visit_parts_not_cancelled` holds any the clinic system refused, which are still
 * in its agenda.
 */
export interface CancelledBookingResponse {
  id: string;
  status: "cancelled";
  other_visit_parts_cancelled?: string[];
  other_visit_parts_not_cancelled?: string[];
}

export interface CompositeVisitMarker {
  service_id: string;
  service_name: string;
  /** 1-based. */
  part: number;
  part_count: number;
}

export type BookingPaymentType = "private" | "insurance" | "public_health";

export interface BookingCreatePayload {
  professional_id: string;
  service_id: string;
  /** Multi-location only; backend defaults to the primary location when omitted. */
  location_id?: string | null;
  payment_type: BookingPaymentType;
  start_date_time: string; // ISO 8601
  /**
   * Optional: omit it and the backend derives the end from the chosen
   * professional's own duration for the service. Send one only to book a
   * deliberately custom length.
   */
  end_date_time?: string; // ISO 8601
  booked_for_name?: string | null;
  booked_for_phone?: string | null;
  booked_by_name?: string | null;
  booked_by_relation?: string | null;
  notes?: string | null;
  conversation_id?: string | null;
  patient_birth_date?: string | null;
  insurance_uuid?: string | null;
  /** False books without sending the patient a confirmation. Backend default: true. */
  notify_patient?: boolean;
}

export interface BookingUpdatePayload {
  notes?: string;
  service_id?: string;
  payment_type?: BookingPaymentType;
  /** Only together with `payment_type` (the API rejects it alone). */
  insurance_uuid?: string | null;
}

// Waiting list — patients who already hold an appointment and want an
// earlier one. Backend: app/api/v1/waitlist_entries.py. list_active now
// returns BOTH this feature's own structured entries AND a clinic's
// PMS-native waitlist rows (e.g. synced from Mediconta) side by side -- see
// that method's own doc comment.
export type WaitlistTimeBucket = "morning" | "afternoon" | "evening";
// "toniagent"/"clinic" for a structured entry; an IntegrationSource value
// (e.g. "mediconta") for a PMS-native row -- widened from a two-value union
// now that the list includes both.
export type WaitlistSource = string;
// "expired" is derived by the API: an active entry whose held appointment has passed.
export type WaitlistStatus = "active" | "expired" | "fulfilled" | "cancelled";
export type WaitlistStatusFilter = WaitlistStatus | "all";
export type WaitlistStatusCounts = Record<WaitlistStatusFilter, number>;

export interface WaitlistEntryResponse {
  id: string;
  // Set for an entry staff made from an appointment. Null for a row with no linked patient: one the
  // voice agent made, or a PMS-native row. Those also may have no booking, and
  // professional_id/service_id are resolved best-effort so can come back null.
  patient_id: string | null;
  patient_name: string | null;
  patient_phone: string | null;
  booking_id: string | null;
  service_id: string | null;
  service_name: string | null;
  professional_id: string | null;
  professional_name: string | null;
  // The specialty (PMS activity group) a PMS-native entry is assigned to; shown when the
  // entry has no service. Null when the PMS has no such concept or none was recorded.
  specialty_name: string | null;
  any_professional: boolean;
  any_earlier: boolean;
  preferred_days: number[]; // 1=Mon..7=Sun
  preferred_time_buckets: WaitlistTimeBucket[];
  notes: string | null;
  source: WaitlistSource;
  status: WaitlistStatus;
  // Who created this row -- distinct from source (which system it's tied to), and what decides
  // who manages it. "toniagent": the voice agent made it (alone, or also pushed to the PMS).
  // "vendor": synced from the clinic's own PMS, added there directly by staff -- managed in the
  // PMS. "clinic": staff made it here. Staff may edit and remove "clinic" and "toniagent"
  // entries -- see isStaffManageable.
  origin: string;
  // The call a voice-agent join came from; null for an entry staff made.
  conversation_id: string | null;
  // Last day (clinic calendar, "YYYY-MM-DD") the entry is still wanted; null = never expires.
  expires_on: string | null;
  current_start_date_time: string | null;
  current_end_date_time: string | null;
  created_at: string;
  updated_at: string;
}

export interface WaitlistEntryCreatePayload {
  booking_id: string;
  any_professional?: boolean;
  any_earlier?: boolean;
  preferred_days?: number[];
  preferred_time_buckets?: WaitlistTimeBucket[];
  notes?: string | null;
}

export interface WaitlistEntryUpdatePayload {
  any_professional?: boolean;
  any_earlier?: boolean;
  preferred_days?: number[];
  preferred_time_buckets?: WaitlistTimeBucket[];
  notes?: string | null;
}

// Slot search — the Agendas Calendar's "find available slot" panel. Same
// engine/contract as the ElevenLabs voice tool's checkAvailability, but
// unscoped from the agent: a service/professional the clinic hid from the
// phone agent, or marked "reception books this by hand", still searches and
// returns normally here (see control-panel-api's SlotFinder.find_slots
// for_agent param).
export interface SlotSearchTimeRange {
  start: string; // "HH:MM"
  end?: string; // "HH:MM", exclusive
}

export interface SlotSearchPayload {
  service_id: string;
  payment_type?: BookingPaymentType;
  professional_id?: string;
  preferred_days?: number[]; // 1=Mon .. 7=Sun
  preferred_time_slots?: Array<"morning" | "afternoon">;
  preferred_time_ranges?: SlotSearchTimeRange[];
  earliest_date?: string; // "YYYY-MM-DD"
  latest_date?: string; // "YYYY-MM-DD"
  location_id?: string;
  // Backend default is 1 (the voice agent only ever needs one at a time) —
  // always send an explicit, higher value for the panel's own browsable list.
  max_results?: number;
}

export interface AvailableSlot {
  professional_id: string;
  professional_name: string;
  start_date_time_human: string;
  service_duration: number; // minutes
  start_date_time_iso: string | null;
  end_date_time_iso: string | null;
  location_id?: string;
  location_name?: string;
}

export interface SlotSearchResult {
  slots: AvailableSlot[];
  today: string | null;
  searched: string | null;
  context_for_response: string | null;
}

export interface ActivityLogEntry {
  id: string;
  conversation_id: string;
  event_type: string;
  task_id: string | null;
  task_type: string | null;
  booking_id: string | null;
  actor_id: string | null;
  actor_name: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  booking_service_name: string | null;
  booking_professional_name: string | null;
  booking_booked_for_name: string | null;
  booking_start_date_time: string | null;
  /** `tasks.auto_review.<action>.<state>` for auto-review tasks, whose stored title is an
   * English engineering string; null for staff- and agent-tool tasks, whose title is the
   * caller's own words. Joined live from `agent_tasks`, so it resolves for old rows too. */
  task_i18n_key: string | null;
}

export interface AgentTaskUpdate {
  title?: string;
  description?: string;
  status?: string;
  priority?: string;
  assigned_to?: string;
  due_date?: string;
  patient_phone?: string;
  patient_name?: string;
  notes?: string;
  completed_at?: string;
  completed_by?: string;
}

// Google Calendar Integration
export interface GoogleAccount {
  id: string; // UUID
  user_email: string;
  client_id: string | null;
  google_account_email: string;
  token_expiry: string; // ISO timestamp
  scope: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CalendarIntegration {
  id: string; // UUID
  google_account_id: string; // UUID
  calendar_id: string;
  calendar_name: string;
  calendar_summary: string | null;
  is_primary: boolean;
  timezone: string | null;
  background_color: string | null;
  foreground_color: string | null;
  last_synced_at: string | null; // ISO timestamp
  sync_status: string; // 'pending', 'syncing', 'completed', 'failed'
  sync_error: string | null;
  webhook_channel_id: string | null;
  webhook_resource_id: string | null;
  webhook_expiration: string | null; // ISO timestamp
  webhook_url: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface GoogleCalendarAttendee {
  email: string;
  displayName?: string;
  responseStatus: 'needsAction' | 'declined' | 'tentative' | 'accepted';
}

export interface CalendarEvent {
  id: string; // UUID
  integration_id: string; // UUID
  google_event_id: string;
  summary: string;
  description: string | null;
  location: string | null;
  start_time: string; // ISO timestamp
  end_time: string; // ISO timestamp
  is_all_day: boolean;
  status: string; // 'confirmed', 'tentative', 'cancelled'
  organizer_email: string | null;
  attendees: GoogleCalendarAttendee[] | null;
  recurrence: string[] | null; // RRULE format strings
  html_link: string | null;
  created_at: string;
  updated_at: string;
}

export interface CalendarEventsListResponse {
  events: CalendarEvent[];
  total: number;
  page: number;
  page_size: number;
}

export interface GoogleCalendarInfo {
  id: string;
  summary: string;
  description: string | null;
  time_zone: string | null;
  primary: boolean;
  background_color: string | null;
  foreground_color: string | null;
}

export interface OAuthStartResponse {
  authorization_url: string;
  state: string;
}

export interface AvailableCalendarsResponse {
  google_account_id: string;
  google_account_email: string;
  calendars: GoogleCalendarInfo[];
}

export interface GoogleAccountWithCalendars {
  account: GoogleAccount;
  calendars: CalendarIntegration[];
}

/** The Google Calendar integration counts as ON only with an active account. */
export const hasActiveGoogleAccount = (accounts: GoogleAccountWithCalendars[]): boolean =>
  accounts.some(({ account }) => account.is_active);

export interface CalendarSelectionRequest {
  calendar_ids: string[];
}

export interface OAuthCallbackRequest {
  code: string;
  state: string;
}

export interface AgentDeploymentResponse {
  id?: string;
  agent_id?: string;
  status: string;
  deployed_at?: string | null;
  failed_at?: string | null;
  last_error?: string | null;
  builder_version?: string | null;
  skipped?: boolean;
}

// Knowledge Base
/** Everything Configuration > Clinic shows, from GET /clinic-settings. */
export interface ClinicSettings {
  client: Client;
  agent_config: AgentConfig;
  serin_iom_active: boolean;
  mediconta_active: boolean;
  /** Optional until every deployed backend sends it. */
  radioweb_active?: boolean;
  insurance_count: number;
  location_count: number;
  primary_location_address: string | null;
  business_hours_exceptions: BusinessHoursException[];
  /** Null unless the request asked for `include=knowledge_base`. */
  knowledge_base_files: KnowledgeBaseFileResponse[] | null;
}

export interface KnowledgeBaseFileResponse {
  id: string;
  file_name: string;
  content_type: string;
  file_size: number;
  status: string;
  stale: boolean;
  elevenlabs_document_id: string | null;
  last_synced_at: string | null;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  /** Null when the response does not carry the file's history (e.g. clinic settings). */
  current_version?: KnowledgeBaseFileVersionResponse | null;
  version_count?: number | null;
}

export interface KnowledgeBaseFileVersionResponse {
  id: string;
  version_number: number;
  file_size: number;
  comment: string | null;
  /** Null for versions uploaded before uploaders were recorded. */
  uploaded_by_email: string | null;
  restored_from_version: number | null;
  created_at: string;
}

export interface KnowledgeBaseUploadUrlRequest {
  file_name: string;
  content_type: string;
  file_size: number;
  comment?: string;
}

export interface KnowledgeBaseNewVersionRequest {
  content_type: string;
  file_size: number;
  comment: string;
}

export interface KnowledgeBaseNewVersionResponse {
  upload_url: string;
  version_id: string;
}

export interface KnowledgeBaseUploadUrlResponse {
  upload_url: string;
  s3_key: string;
  file_id: string;
}

export interface PronunciationDictionaryFileResponse {
  id: string;
  file_name: string;
  content_type: string;
  file_size: number;
  status: string;
  stale: boolean;
  elevenlabs_dictionary_id: string | null;
  elevenlabs_version_id: string | null;
  last_synced_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface PronunciationDictionaryUploadUrlRequest {
  file_name: string;
  content_type: string;
  file_size: number;
}

export interface PronunciationDictionaryUploadUrlResponse {
  upload_url: string;
  s3_key: string;
  file_id: string;
}

// API client
/** The filters `GET /calls` and `GET /calls/summary` share, under the same names. */
function callsFilterSearchParams(params?: CallsSummaryParams): URLSearchParams {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.append("status", params.status);
  if (params?.type) searchParams.append("type", params.type);
  if (params?.start_date) searchParams.append("start_date", params.start_date);
  if (params?.end_date) searchParams.append("end_date", params.end_date);
  if (params?.handled_by) searchParams.append("handled_by", params.handled_by);
  if (params?.channel) searchParams.append("channel", params.channel);
  if (params?.direction) searchParams.append("direction", params.direction);
  if (params?.search) searchParams.append("search", params.search);
  if (params?.search_in_summary) searchParams.append("search_in_summary", "true");
  if (params?.search_in_name) searchParams.append("search_in_name", "true");
  if (params?.unreached) searchParams.append("unreached", "true");
  return searchParams;
}

export const api = {
  // Invitations
  createInvitation: (data: CreateInvitationRequest) =>
    apiRequest<Invitation>("/invitations", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  listInvitations: (params?: ListInvitationsParams) => {
    const search = new URLSearchParams();
    if (params?.status) search.append("status_filter", params.status);
    if (params?.search) search.append("search", params.search);
    if (params?.skip !== undefined) search.append("skip", params.skip.toString());
    if (params?.limit !== undefined) search.append("limit", params.limit.toString());
    const queryString = search.toString();
    return apiRequest<Paginated<Invitation>>(
      `/invitations${queryString ? `?${queryString}` : ""}`
    );
  },

  validateInvitation: (token: string) =>
    publicApiRequest<ValidateInvitationResponse>(`/invitations/validate?token=${token}`),

  completeSignup: (invitationToken: string) =>
    publicApiRequest<CompleteSignupResponse>("/auth/complete-signup-public", {
      method: "POST",
      body: JSON.stringify({ invitation_token: invitationToken }),
    }),

  revokeInvitation: (invitationId: string) =>
    apiRequest<null>(`/invitations/${invitationId}`, {
      method: "DELETE",
    }),

  // Users
  listUsers: (params?: ListUsersParams) => {
    const search = new URLSearchParams();
    if (params?.search) search.append("search", params.search);
    if (params?.skip !== undefined) search.append("skip", params.skip.toString());
    if (params?.limit !== undefined) search.append("limit", params.limit.toString());
    const queryString = search.toString();
    return apiRequest<Paginated<CognitoUser>>(`/users${queryString ? `?${queryString}` : ""}`);
  },

  getUser: (username: string) =>
    apiRequest<CognitoUser>(`/users/${username}`),

  updateUser: (username: string, data: UpdateUserRequest) =>
    apiRequest<CognitoUser>(`/users/${username}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  deleteUser: (username: string) =>
    apiRequest<null>(`/users/${username}`, {
      method: "DELETE",
    }),

  resetUserPassword: (username: string) =>
    apiRequest<{ message: string }>(`/users/${username}/reset-password`, {
      method: "POST",
    }),

  disableUser: (username: string) =>
    apiRequest<null>(`/users/${username}/disable`, {
      method: "POST",
    }),

  enableUser: (username: string) =>
    apiRequest<null>(`/users/${username}/enable`, {
      method: "POST",
    }),

  updateUserGroups: (username: string, groups: string[]) =>
    apiRequest<CognitoUser>(`/users/${username}/groups`, {
      method: "PUT",
      body: JSON.stringify({ groups }),
    }),

  // Clients
  createClient: (data: CreateClientRequest) =>
    apiRequest<Client>("/clients", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  listClients: (params?: ListClientsParams) => {
    const search = new URLSearchParams();
    if (params?.skip !== undefined) search.append("skip", params.skip.toString());
    if (params?.limit !== undefined) search.append("limit", params.limit.toString());
    if (params?.includeDeleted) search.append("include_deleted", "true");
    if (params?.search) search.append("search", params.search);
    const queryString = search.toString();
    return apiRequest<Paginated<Client>>(`/clients${queryString ? `?${queryString}` : ""}`);
  },

  /**
   * Every active client, following pagination to the end.
   *
   * For admin pickers that have no search box: `listClients` returns one page, so a
   * client past the first page would be permanently unreachable in such a dropdown.
   * Prefer `listClients` with a `search` term wherever the UI can offer one.
   */
  listAllClients: async (): Promise<Client[]> => {
    const PAGE = 100;
    const all: Client[] = [];
    for (let skip = 0; ; skip += PAGE) {
      const page = (await api.listClients({ skip, limit: PAGE })).items;
      all.push(...page);
      if (page.length < PAGE) break;
    }
    return all.filter((c) => !c.is_deleted);
  },

  getClient: (clientId: string) =>
    apiRequest<Client>(`/clients/${clientId}`),

  updateClient: (clientId: string, data: UpdateClientRequest) =>
    apiRequest<Client>(`/clients/${clientId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  deleteClient: (clientId: string) =>
    apiRequest<null>(`/clients/${clientId}`, {
      method: "DELETE",
    }),

  restoreClient: (clientId: string) =>
    apiRequest<Client>(`/clients/${clientId}/restore`, {
      method: "POST",
    }),

  getTwilioPhoneNumbers: () =>
    apiRequest<TwilioPhoneNumberListResponse>("/twilio-phones"),

  getWhatsAppAccounts: () =>
    apiRequest<WhatsAppAccountListResponse>("/whatsapp-accounts"),

  getClientWhatsAppNumber: (clientId: string) =>
    apiRequest<WhatsAppNumberStatus>(
      `/admin/clients/${clientId}/whatsapp-number`,
    ),

  /**
   * Adds the clinic's ToniAgent number to our WABA and asks Meta to phone it.
   *
   * Returns 202: the number exists when this resolves but is NOT verified yet. Meta calls
   * the line seconds to minutes later and a worker reads the code out of that call, so the
   * caller has to poll `getClientWhatsAppNumber` for the outcome.
   */
  provisionClientWhatsAppNumber: (
    clientId: string,
    body: { verified_name?: string } = {},
  ) =>
    apiRequest<WhatsAppNumberStatus>(
      `/admin/clients/${clientId}/whatsapp-number/provision`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  getWhatsAppMessages: (filters: WhatsAppMessageLogFilters = {}) => {
    const query = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== "") query.set(key, String(value));
    });
    const suffix = query.toString() ? `?${query}` : "";
    return apiRequest<Paginated<WhatsAppMessageLog>>(
      `/admin/whatsapp-messages${suffix}`,
    );
  },

  /**
   * Resend a FAILED end-of-call notice on its own row, exactly as first attempted. Resolves
   * whether ElevenLabs accepts it or rejects it again — `status` says which.
   */
  retryWhatsAppMessage: (messageId: string) =>
    apiRequest<WhatsAppMessageRetryResult>(`/admin/whatsapp-messages/${messageId}/retry`, {
      method: "POST",
    }),

  /** Every template name that appears in the log — the options for its template filter. */
  getWhatsAppTemplateNames: () =>
    apiRequest<string[]>("/admin/whatsapp-messages/template-names"),

  getSmsMessages: (filters: SmsMessageLogFilters = {}) => {
    const query = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== "") query.set(key, String(value));
    });
    const suffix = query.toString() ? `?${query}` : "";
    return apiRequest<Paginated<SmsMessageLog>>(`/admin/sms-messages${suffix}`);
  },

  sendWhatsAppTemplate: (data: WhatsAppSendRequest) =>
    apiRequest<WhatsAppSendResult>("/admin/whatsapp-send", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  getWhatsAppManagers: (clientId: string) =>
    apiRequest<WhatsAppManager[]>(`/admin/clients/${clientId}/whatsapp-managers`),

  createWhatsAppManager: (clientId: string, data: CreateWhatsAppManagerRequest) =>
    apiRequest<WhatsAppManager>(`/admin/clients/${clientId}/whatsapp-managers`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  updateWhatsAppManager: (
    clientId: string,
    managerId: string,
    data: UpdateWhatsAppManagerRequest,
  ) =>
    apiRequest<WhatsAppManager>(
      `/admin/clients/${clientId}/whatsapp-managers/${managerId}`,
      { method: "PATCH", body: JSON.stringify(data) },
    ),

  deleteWhatsAppManager: (clientId: string, managerId: string) =>
    apiRequest<void>(`/admin/clients/${clientId}/whatsapp-managers/${managerId}`, {
      method: "DELETE",
    }),

  // Clinic-facing twins of the four above, for the "Clinic staff" block on
  // /config/clinic. No clientId: the backend takes it from the caller's JWT, so a
  // clinic can only ever read and edit its own staff.
  // Chat with the clinic's agent. Each session is one new ElevenLabs conversation;
  // `chatId` continues an existing chat, omitted it starts one.
  startStaffAgentSession: (chatId: string | null) =>
    apiRequest<import("@/types/agentChat").StaffAgentSession>("/staff-agent-chat/sessions", {
      method: "POST",
      body: JSON.stringify({ chat_id: chatId }),
    }),

  listStaffAgentChats: () =>
    apiRequest<import("@/types/agentChat").StaffAgentChat[]>("/staff-agent-chat/chats"),

  titleStaffAgentChat: (chatId: string, title: string) =>
    apiRequest<void>(`/staff-agent-chat/chats/${chatId}`, {
      method: "PATCH",
      body: JSON.stringify({ title }),
    }),

  getStaffAgentChatMessages: (chatId: string) =>
    apiRequest<import("@/types/agentChat").StaffAgentChatTranscript>(
      `/staff-agent-chat/chats/${chatId}/messages`
    ),

  getOwnWhatsAppManagers: () => apiRequest<WhatsAppManager[]>("/whatsapp-managers"),

  // Also grants agent access and welcomes them — see CreateWhatsAppManagerRequest.
  createOwnWhatsAppManager: (data: CreateWhatsAppManagerRequest) =>
    apiRequest<WhatsAppManager>("/whatsapp-managers", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  updateOwnWhatsAppManager: (managerId: string, data: UpdateWhatsAppManagerRequest) =>
    apiRequest<WhatsAppManager>(`/whatsapp-managers/${managerId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  deleteOwnWhatsAppManager: (managerId: string) =>
    apiRequest<void>(`/whatsapp-managers/${managerId}`, { method: "DELETE" }),

  /**
   * Owner/admin only — a member gets a 403; the staff card hides the control for them.
   *
   * Creating a staff member already grants this, so the clinic reaches it only to edit the
   * professional link or the expiry (which re-sends `granted: true`, and is also how a lapsed
   * grant comes back). `granted: false` has no caller in the UI: withdrawing the access means
   * removing the person. It stays on the API for support work.
   */
  setAgentAccess: (managerId: string, data: SetAgentAccessRequest) =>
    apiRequest<WhatsAppManager>(`/whatsapp-managers/${managerId}/agent-access`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  getWhatsAppTemplates: () =>
    apiRequest<WhatsAppTemplate[]>("/admin/whatsapp-templates"),

  /** The clinic's deployed agents by role. Empty when it has never been deployed. */
  getClientAgents: (clientId: string) =>
    apiRequest<ClientAgent[]>(`/admin/clients/${clientId}/agents`),

  /**
   * Enqueues a push of any code-defined template Meta doesn't have yet. Returns 202 with
   * the queued run — poll `getWhatsAppTemplateSyncStatus()` for the outcome. Asking the
   * server to do this inline timed out at 30s, which is the API Gateway's own cap.
   *
   * A sync already in flight is returned as-is rather than started twice.
   */
  syncWhatsAppTemplates: () =>
    apiRequest<WhatsAppTemplateSyncRun>("/admin/whatsapp-templates/sync", {
      method: "POST",
    }),

  getWhatsAppTemplateSyncStatus: () =>
    apiRequest<WhatsAppTemplateSyncRun>("/admin/whatsapp-templates/sync"),

  deleteWhatsAppTemplate: (templateId: string) =>
    apiRequest<void>(`/admin/whatsapp-templates/${templateId}`, {
      method: "DELETE",
    }),

  /** Completes the Phase 3 Embedded Signup connect flow — stores the WABA id this
   * clinic just granted our Business Portfolio partner access to. Path-keyed by
   * clientId (admin browsing an arbitrary client), not the caller's own JWT. */
  connectWhatsAppWaba: (clientId: string, data: ConnectWhatsAppWabaRequest) =>
    apiRequest<ConnectWhatsAppWabaResult>(
      `/admin/clients/${clientId}/whatsapp-waba/connect`,
      { method: "POST", body: JSON.stringify(data) },
    ),

  // The `/whatsapp-waba` routes resolve their client from `get_client_id`, which honours
  // `X-Client-Id` for an admin. Passing `actingFor` therefore points any of them at one
  // named client — that is what lets the admin's client page own the Meta grant. The global
  // impersonation header is applied first in `apiRequestCore` and `options.headers` is
  // spread after it, so this wins over whatever client the sidebar is impersonating.
  //
  // Meta still requires whoever completes the Embedded Signup popup to be an admin of the
  // CLINIC's own Business Portfolio: this changes which client the grant is STORED against,
  // never whose Meta account the popup grants.
  getOwnWhatsAppWaba: (actingFor?: string) =>
    apiRequest<WhatsAppWabaStatus>("/whatsapp-waba", asClient(actingFor)),

  connectOwnWhatsAppWaba: (data: ConnectWhatsAppWabaRequest, actingFor?: string) =>
    apiRequest<ConnectWhatsAppWabaResult>("/whatsapp-waba/connect", {
      method: "POST",
      body: JSON.stringify(data),
      ...asClient(actingFor),
    }),

  getOwnWhatsAppTemplates: (actingFor?: string) =>
    apiRequest<OwnWhatsAppTemplate[]>("/whatsapp-waba/templates", asClient(actingFor)),

  // Always sent, and defaults to true here as well as server-side: deleting a template is
  // irreversible in Meta and re-approval needs a fresh review, so every layer has to fail
  // towards keeping them.
  disconnectOwnWhatsAppWaba: (keepTemplates = true, actingFor?: string) =>
    apiRequest<DisconnectWhatsAppWabaResult>(
      `/whatsapp-waba?keep_templates=${keepTemplates}`,
      { method: "DELETE", ...asClient(actingFor) }
    ),

  updateClientWithSubscription: (
    clientId: string,
    request: UpdateClientWithSubscriptionRequest
  ) =>
    apiRequest<ClientWithSubscriptionResponse>(
      `/clients/${clientId}/with-subscription`,
      {
        method: "PATCH",
        body: JSON.stringify(request),
      }
    ),

  // Subscriptions
  createSubscription: (data: CreateSubscriptionRequest) =>
    apiRequest<Subscription>("/subscriptions", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  getSubscription: (subscriptionId: string) =>
    apiRequest<Subscription>(`/subscriptions/${subscriptionId}`),

  getActiveSubscription: () =>
    apiRequest<Subscription | null>("/subscriptions/active"),

  listClientSubscriptions: (clientId: string) =>
    apiRequest<Subscription[]>(`/subscriptions/client/${clientId}`),

  updateSubscription: (subscriptionId: string, data: UpdateSubscriptionRequest) =>
    apiRequest<Subscription>(`/subscriptions/${subscriptionId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  deactivateSubscription: (subscriptionId: string) =>
    apiRequest<void>(`/subscriptions/${subscriptionId}`, {
      method: "DELETE",
    }),

  // Calls
  listCalls: (params?: ListCallsParams) => {
    const searchParams = callsFilterSearchParams(params);
    if (params?.agent_id) searchParams.append("agent_id", params.agent_id);
    if (params?.quality_verdict?.length) {
      params.quality_verdict.forEach((v) => searchParams.append("quality_verdict", v));
    }
    if (params?.min_score !== undefined) searchParams.append("min_score", params.min_score.toString());
    if (params?.max_score !== undefined) searchParams.append("max_score", params.max_score.toString());
    if (params?.fired_rule?.length) {
      params.fired_rule.forEach((r) => searchParams.append("fired_rule", r));
    }
    if (params?.order_by) searchParams.append("order_by", params.order_by);
    if (params?.order_dir) searchParams.append("order_dir", params.order_dir);
    if (params?.skip !== undefined) searchParams.append("skip", params.skip.toString());
    if (params?.limit !== undefined) searchParams.append("limit", params.limit.toString());
    if (params?.view) searchParams.append("view", params.view);
    if (params?.include_summary !== undefined) {
      searchParams.append("include_summary", String(params.include_summary));
    }
    if (params?.include_facets !== undefined) {
      searchParams.append("include_facets", String(params.include_facets));
    }
    const queryString = searchParams.toString();
    return apiRequest<PaginatedCallsResponse>(`/calls${queryString ? `?${queryString}` : ""}`);
  },

  /** The `/conversations` tab bubbles and needs-attention banner, in one request. */
  getCallsSummary: (params: CallsSummaryParams) => {
    const queryString = callsFilterSearchParams(params).toString();
    return apiRequest<CallsSummaryResponse>(`/calls/summary${queryString ? `?${queryString}` : ""}`);
  },

  getMonthlyStats: () => apiRequest<MonthlyStatsRow[]>("/admin/monthly-stats"),

  triggerHolidaySync: () =>
    apiRequest<{
      locations: number;
      holidays_written: number;
      failures: string[];
    }>(`/admin/holidays/sync`, { method: "POST" }),

  getAgentHealthSummary: (clientId: string) =>
    apiRequest<AgentHealthSummaryResponse>(
      `/admin/agent-health/summary?client_id=${encodeURIComponent(clientId)}`,
    ),

  /**
   * One professional's real slot probe. Deliberately per-professional: probing a
   * whole clinic in one request times out on exactly the broken clinics this
   * page exists to find (no availability = a full 365-day scan each).
   */
  getAgentHealthAvailability: (clientId: string, professionalId: string) =>
    apiRequest<AgentHealthAvailabilityResponse>(
      `/admin/agent-health/availability?client_id=${encodeURIComponent(clientId)}` +
        `&professional_id=${encodeURIComponent(professionalId)}`,
    ),

  /**
   * Service names the agent may not be able to tell apart, judged by Haiku 4.5.
   *
   * Its own request because it spends a Bedrock call: keeping it off the summary
   * path means every run isn't waiting on an LLM, and a Bedrock outage degrades
   * one panel instead of the whole report.
   */
  getAgentHealthServiceNames: (clientId: string) =>
    apiRequest<AgentHealthServiceNamingResponse>(
      `/admin/agent-health/service-names?client_id=${encodeURIComponent(clientId)}`,
    ),

  // Admin: batch ElevenLabs agent deploy
  listAgentDeployments: () =>
    apiRequest<
      Array<{
        client_id: string;
        builder_version: string | null;
        status: string;
        deployed_at: string | null;
        pinned_builder_version: string | null;
      }>
    >(`/admin/agent-deploy/deployments`),

  deployAgentsBatch: (
    body: ({ all_clients: true } | { client_ids: string[] }) & {
      builder_version?: string;
    },
  ) =>
    apiRequest<{
      dispatched: boolean;
      target_count: number;
      client_ids: string[];
      skipped_client_ids: string[];
    }>(`/admin/agent-deploy/batch`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  listClientBuilderVersions: (clientId: string) =>
    apiRequest<
      Array<{
        builder_version: string;
        last_deployed_at: string | null;
        is_current: boolean;
        date: string | null;
        pr: number | null;
        summary: string | null;
      }>
    >(`/admin/agent-deploy/clients/${clientId}/versions`),

  listFleetBuilderVersions: () =>
    apiRequest<
      Array<{
        builder_version: string;
        last_deployed_at: string | null;
        is_current: boolean;
        date: string | null;
        pr: number | null;
        summary: string | null;
        eligible_client_ids: string[];
      }>
    >(`/admin/agent-deploy/versions`),

  // Admin: pause / resume every request to one PMS vendor
  listPmsStates: () => apiRequest<PmsState[]>(`/admin/pms`),

  listPmsClinics: (source: string) =>
    apiRequest<PmsClinic[]>(`/admin/pms/${encodeURIComponent(source)}/clinics`),

  pausePms: (source: string, body: { reason: string; force_voice_shadow: boolean }) =>
    apiRequest<null>(`/admin/pms/${encodeURIComponent(source)}/pause`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  resumePms: (source: string) =>
    apiRequest<null>(`/admin/pms/${encodeURIComponent(source)}/resume`, { method: "POST" }),

  setClientBuilderPin: (clientId: string, builderVersion: string | null) =>
    apiRequest<{ client_id: string; pinned_builder_version: string | null }>(
      `/admin/agent-deploy/clients/${clientId}/pin`,
      {
        method: "PUT",
        body: JSON.stringify({ builder_version: builderVersion }),
      },
    ),

  /** With `include`, the response also carries the requested conversation-scoped lists. */
  getCall: (callId: string, options?: { include?: CallDetailInclude[] }) => {
    const include = options?.include?.length ? `?include=${options.include.join(",")}` : "";
    return apiRequest<CallDetail>(`/calls/${callId}${include}`);
  },

  /** Admin-only: erases the conversation, its recording, transcript and internal notes. */
  deleteCall: (callId: string) =>
    apiRequest<null>(`/calls/${callId}`, { method: "DELETE" }),

  getCallOwner: (callId: string) =>
    apiRequest<CallOwner>(`/calls/${callId}/owner`),

  getCallFeatureFlags: (callId: string) =>
    apiRequest<CallFeatureFlags>(`/calls/${callId}/feature-flags`),

  /** Admin-only (403 otherwise): every tool the call's agent invoked, oldest first. */
  getCallToolInvocations: (callId: string) =>
    apiRequest<CallToolInvocations>(`/calls/${callId}/tool-invocations`),

  getCallByConversation: (conversationId: string) =>
    apiRequest<Call>(`/calls/conversation/${conversationId}`),

  getCallTranscription: (callId: string) =>
    apiRequest<TranscriptionResponse>(`/calls/${callId}/transcription`),

  updateCallStatus: (callId: string, status: string) =>
    apiRequest<Call>(`/calls/${callId}`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    }),

  listCallExtra: (callIds: string[]) =>
    apiRequest<CallExtraData[]>(
      `/mvp-suministros/call-extra?call_ids=${callIds.join(",")}`,
    ),

  // Services
  createService: (data: CreateServiceRequest) =>
    apiRequest<Service>("/services", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  listServices: (params?: ListServicesParams) => {
    const search = new URLSearchParams();
    if (params?.includeDeleted) search.append("include_deleted", "true");
    if (params?.search) search.append("search", params.search);
    if (params?.skip !== undefined) search.append("skip", params.skip.toString());
    if (params?.limit !== undefined) search.append("limit", params.limit.toString());
    if (params?.orderBy) search.append("order_by", params.orderBy);
    if (params?.orderDir) search.append("order_dir", params.orderDir);
    const queryString = search.toString();
    return apiRequest<Paginated<Service>>(`/services${queryString ? `?${queryString}` : ""}`).then((page) =>
      params?.orderBy ? page : sortPageByName(page)
    );
  },

  createServiceVariant: (parentServiceId: string, data: CreateServiceVariantRequest) =>
    apiRequest<Service>(`/services/${parentServiceId}/variants`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  createCompositeService: (data: CreateCompositeServiceRequest) =>
    apiRequest<Service>("/services/composites", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  updateServiceComposition: (serviceId: string, data: UpdateServiceCompositionRequest) =>
    apiRequest<Service>(`/services/${serviceId}/composition`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  getService: (serviceId: string) =>
    apiRequest<Service>(`/services/${serviceId}`),

  updateService: (serviceId: string, data: UpdateServiceRequest) =>
    apiRequest<Service>(`/services/${serviceId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  deleteService: (serviceId: string, hard: boolean = false) =>
    apiRequest<void>(`/services/${serviceId}${hard ? '?hard=true' : ''}`, {
      method: "DELETE",
    }),

  restoreService: (serviceId: string) =>
    apiRequest<Service>(`/services/${serviceId}/restore`, {
      method: "POST",
    }),

  /** A copy named "<name> (cloned)" with the same configuration, hidden from the agent. */
  cloneService: (serviceId: string) =>
    apiRequest<Service>(`/services/${serviceId}/clone`, {
      method: "POST",
    }),

  // Professionals
  createProfessional: (data: CreateProfessionalRequest) =>
    apiRequest<Professional>("/professionals", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  listProfessionals: (params?: ListProfessionalsParams) => {
    const search = new URLSearchParams();
    if (params?.includeDeleted) search.append("include_deleted", "true");
    if (params?.search) search.append("search", params.search);
    if (params?.skip !== undefined) search.append("skip", params.skip.toString());
    if (params?.limit !== undefined) search.append("limit", params.limit.toString());
    if (params?.orderBy) search.append("order_by", params.orderBy);
    if (params?.orderDir) search.append("order_dir", params.orderDir);
    const queryString = search.toString();
    return apiRequest<Paginated<Professional>>(
      `/professionals${queryString ? `?${queryString}` : ""}`
    ).then((page) => (params?.orderBy ? page : sortPageByName(page)));
  },

  getProfessional: (professionalId: string) =>
    apiRequest<Professional>(`/professionals/${professionalId}`),

  updateProfessional: (professionalId: string, data: UpdateProfessionalRequest) =>
    apiRequest<Professional>(`/professionals/${professionalId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  deleteProfessional: (professionalId: string, hard: boolean = false) =>
    apiRequest<void>(`/professionals/${professionalId}${hard ? '?hard=true' : ''}`, {
      method: "DELETE",
    }),

  restoreProfessional: (professionalId: string) =>
    apiRequest<Professional>(`/professionals/${professionalId}/restore`, {
      method: "POST",
    }),

  /** A copy named "<name> (cloned)" with the same services and weekly hours, hidden from the agent. */
  cloneProfessional: (professionalId: string) =>
    apiRequest<Professional>(`/professionals/${professionalId}/clone`, {
      method: "POST",
    }),

  // Admin only. 409 when returning to "sync" while the professional holds more
  // than one active agenda — the detail says which ones must go first.
  updateProfessionalScheduleOwnership: (professionalId: string, scheduleManagedBy: ScheduleOwner) =>
    apiRequest<Professional>(`/professionals/${professionalId}/schedule-ownership`, {
      method: "PUT",
      body: JSON.stringify({ schedule_managed_by: scheduleManagedBy }),
    }),

  // Insurances
  listInsurances: (includeDeleted: boolean = false) =>
    apiRequest<Insurance[]>(`/insurances${includeDeleted ? "?include_deleted=true" : ""}`),

  getInsurance: (insuranceId: string) =>
    apiRequest<Insurance>(`/insurances/${insuranceId}`),

  createInsurance: (data: CreateInsuranceRequest) =>
    apiRequest<Insurance>("/insurances", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  updateInsurance: (insuranceId: string, data: UpdateInsuranceRequest) =>
    apiRequest<Insurance>(`/insurances/${insuranceId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  deleteInsurance: (insuranceId: string, hard: boolean = false) =>
    apiRequest<void>(`/insurances/${insuranceId}${hard ? "?hard=true" : ""}`, {
      method: "DELETE",
    }),

  restoreInsurance: (insuranceId: string) =>
    apiRequest<Insurance>(`/insurances/${insuranceId}/restore`, {
      method: "POST",
    }),

  // Business hours exceptions
  listBusinessHoursExceptions: () =>
    apiRequest<BusinessHoursException[]>("/business-hours-exceptions"),

  createBusinessHoursException: (data: BusinessHoursExceptionRequest) =>
    apiRequest<BusinessHoursException>("/business-hours-exceptions", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  updateBusinessHoursException: (exceptionId: string, data: BusinessHoursExceptionRequest) =>
    apiRequest<BusinessHoursException>(`/business-hours-exceptions/${exceptionId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  deleteBusinessHoursException: (exceptionId: string) =>
    apiRequest<void>(`/business-hours-exceptions/${exceptionId}`, {
      method: "DELETE",
    }),

  // Transfer callers (numbers that reach the clinic, never the agent)
  listTransferCallers: () => apiRequest<TransferCaller[]>("/transfer-callers"),

  addTransferCaller: (data: { phone_number: string; label?: string | null }) =>
    apiRequest<TransferCaller>("/transfer-callers", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  updateTransferCaller: (
    transferCallerId: string,
    changes: { phone_number?: string; label?: string | null }
  ) =>
    apiRequest<TransferCaller>(`/transfer-callers/${transferCallerId}`, {
      method: "PATCH",
      body: JSON.stringify(changes),
    }),

  removeTransferCaller: (transferCallerId: string) =>
    apiRequest<void>(`/transfer-callers/${transferCallerId}`, {
      method: "DELETE",
    }),

  // Locations (multi-location support)
  listLocations: async (includeInactive: boolean = false) => {
    const locations = await apiRequest<Location[]>(
      `/locations${includeInactive ? "?include_inactive=true" : ""}`
    );
    return sortByName(locations ?? []);
  },

  getLocation: (locationId: string) =>
    apiRequest<Location>(`/locations/${locationId}`),

  createLocation: (data: CreateLocationRequest) =>
    apiRequest<Location>("/locations", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  updateLocation: (locationId: string, data: UpdateLocationRequest) =>
    apiRequest<Location>(`/locations/${locationId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  deleteLocation: (locationId: string) =>
    apiRequest<void>(`/locations/${locationId}`, {
      method: "DELETE",
    }),

  setPrimaryLocation: (locationId: string) =>
    apiRequest<Location>(`/locations/${locationId}/primary`, {
      method: "POST",
    }),

  // Location groups: locations in one group are searched together by the agent
  listLocationGroups: async () => {
    const groups = await apiRequest<LocationGroup[]>("/location-groups");
    return sortByName(groups ?? []);
  },

  createLocationGroup: (name: string) =>
    apiRequest<LocationGroup>("/location-groups", {
      method: "POST",
      body: JSON.stringify({ name }),
    }),

  renameLocationGroup: (groupId: string, name: string) =>
    apiRequest<LocationGroup>(`/location-groups/${groupId}`, {
      method: "PATCH",
      body: JSON.stringify({ name }),
    }),

  deleteLocationGroup: (groupId: string) =>
    apiRequest<void>(`/location-groups/${groupId}`, {
      method: "DELETE",
    }),

  // Menu data
  getMenuData: () =>
    apiRequest<import("@/types/menu").MenuDataResponse>("/menu/data"),

  // Partner Portal
  getPartnerMe: () => apiRequest<PartnerProfile>("/partner/me"),

  listPartnerLeads: () => apiRequest<PartnerLead[]>("/partner/leads"),

  createPartnerLead: (data: CreatePartnerLeadRequest) =>
    apiRequest<PartnerLead>("/partner/leads", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  listAdminPartners: () => apiRequest<AdminPartner[]>("/admin/partners"),

  updateAdminPartner: (partnerId: string, body: AdminPartnerUpdate) =>
    apiRequest<AdminPartner>(`/admin/partners/${partnerId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  /** `convertible` keeps only leads a new client can still be created from;
   * `partner_id` narrows the list to one referrer. */
  listAdminPartnerLeads: (params: { convertible?: boolean; partner_id?: string } = {}) => {
    const query = new URLSearchParams();
    if (params.convertible) query.set("convertible", "true");
    if (params.partner_id) query.set("partner_id", params.partner_id);
    // Not `query.size`: undefined on Safari < 17, which would drop both filters.
    const qs = query.toString();
    return apiRequest<AdminPartnerLead[]>(`/admin/partner-leads${qs ? `?${qs}` : ""}`);
  },

  /** A referral sales took by hand, filed under `partnerId`. Same duplicate
   * rule as the portal: 409 when the partner already has that contact open.
   * `client_id` regularises history: the clinic was already working with us,
   * so the referral is filed as signed against that client. */
  createAdminPartnerLead: (partnerId: string, data: CreateAdminPartnerLeadRequest) =>
    apiRequest<AdminPartnerLead>(`/admin/partners/${partnerId}/leads`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  updateAdminPartnerLead: (leadId: string, body: AdminPartnerLeadUpdate) =>
    apiRequest<AdminPartnerLead>(`/admin/partner-leads/${leadId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  /** Erases a referral so it can be filed again — the admin way to correct one
   * that named the wrong partner or clinic. Also undoes it in HubSpot. */
  deleteAdminPartnerLead: (leadId: string) =>
    apiRequest<null>(`/admin/partner-leads/${leadId}`, {
      method: "DELETE",
    }),

  // Agent Configuration
  getAgentConfig: (clientId: string) =>
    apiRequest<AgentConfig>(`/clients/${clientId}/agent-config`),

  updateAgentConfig: (clientId: string, data: UpdateAgentConfigRequest) =>
    apiRequest<AgentConfig>(`/clients/${clientId}/agent-config`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  getClinicSettings: ({ includeKnowledgeBase }: { includeKnowledgeBase: boolean }) =>
    apiRequest<ClinicSettings>(`/clinic-settings${includeKnowledgeBase ? "?include=knowledge_base" : ""}`),

  // Holidays (location selectors + next-5 preview)
  getUpcomingHolidays: (locale?: string) =>
    apiRequest<UpcomingHoliday[]>(
      `/holidays/upcoming${locale ? `?locale=${encodeURIComponent(locale)}` : ""}`
    ),

  getHolidayProvinces: async (country: string) => {
    const provinces = await apiRequest<HolidayProvinceOption[]>(
      `/holidays/provinces?country=${encodeURIComponent(country)}`
    );
    return sortByName(provinces ?? []);
  },

  getHolidayLocalities: async (province: string, query?: string) => {
    const localities = await apiRequest<LocalityOption[]>(
      `/holidays/localities?province=${encodeURIComponent(province)}` +
        (query ? `&q=${encodeURIComponent(query)}` : "")
    );
    return sortByName(localities ?? []);
  },

  getRecordingNoticeAudioUploadUrl: (clientId: string) =>
    apiRequest<{ upload_url: string; s3_key: string }>(
      `/clients/${clientId}/agent-config/recording-notice-audio/upload-url`,
      { method: "POST" }
    ),

  confirmRecordingNoticeAudio: (clientId: string) =>
    apiRequest<AgentConfig>(
      `/clients/${clientId}/agent-config/recording-notice-audio/confirm`,
      { method: "POST" }
    ),

  deleteRecordingNoticeAudio: (clientId: string) =>
    apiRequest<AgentConfig>(
      `/clients/${clientId}/agent-config/recording-notice-audio`,
      { method: "DELETE" }
    ),

  getRecordingNoticeAudioUrl: (clientId: string) =>
    apiRequest<{ url: string }>(
      `/clients/${clientId}/agent-config/recording-notice-audio/url`
    ),

  // Bookings
  listBookings: (params?: {
    conversation_id?: string;
    /** Local calendar date "YYYY-MM-DD" — the calendar day-view's filter. Ignored if conversation_id is also set. */
    date?: string;
    /** Inclusive range end "YYYY-MM-DD" — with `date`, the calendar week-view's filter. */
    date_to?: string;
    professional_id?: string;
    /** Restricts the response to these professionals, sent as a repeated `professional_id`. */
    professional_ids?: string[];
    service_id?: string;
    location_id?: string;
  }, options?: { signal?: AbortSignal }) => {
    const search = new URLSearchParams();
    if (params?.conversation_id) search.append("conversation_id", params.conversation_id);
    if (params?.date) search.append("date", params.date);
    if (params?.date_to) search.append("date_to", params.date_to);
    if (params?.professional_id) search.append("professional_id", params.professional_id);
    params?.professional_ids?.forEach((id) => search.append("professional_id", id));
    if (params?.service_id) search.append("service_id", params.service_id);
    if (params?.location_id) search.append("location_id", params.location_id);
    const queryString = search.toString();
    return apiRequest<BookingResponse[]>(`/bookings${queryString ? `?${queryString}` : ""}`, options);
  },

  createBooking: (payload: BookingCreatePayload) =>
    apiRequest<BookingResponse>("/bookings", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  cancelBooking: (
    bookingId: string,
    { onlyThisPart = false, notifyPatient = true }: { onlyThisPart?: boolean; notifyPatient?: boolean } = {}
  ) =>
    apiRequest<CancelledBookingResponse>(`/bookings/${encodeURIComponent(bookingId)}/cancel`, {
      method: "POST",
      body: JSON.stringify({ only_this_part: onlyThisPart, notify_patient: notifyPatient }),
    }),

  // Moves a booking to a new professional/time. Returns a NEW booking (a
  // different id from `bookingId`) — server-side, this cancels the original
  // and creates the replacement, so the patient gets exactly one
  // "rescheduled" notification rather than a "cancelled" one immediately
  // followed by a "confirmed" one. Everything else (patient, service,
  // payment type, notes...) carries over unchanged; only professional/time
  // are settable here. `end_date_time` is optional — omit it to let the
  // server derive it from the (possibly new) professional's own duration for
  // the booking's existing service.
  rescheduleBooking: (
    bookingId: string,
    payload: {
      professional_id: string;
      start_date_time: string;
      end_date_time?: string;
      /** False moves it without telling the patient. Backend default: true. */
      notify_patient?: boolean;
    }
  ) =>
    apiRequest<BookingResponse>(
      `/bookings/${encodeURIComponent(bookingId)}/reschedule`,
      { method: "POST", body: JSON.stringify(payload) }
    ),

  // Staff edit of a native booking's notes, service or payment type — only what is sent
  // changes (`notes: ""` clears the note). Date, time and professional stay on
  // `rescheduleBooking`. A booking whose service/payment live in a PMS or Google Calendar
  // event answers 422 with the reason; notes are always editable. The response carries no
  // joined names, so callers merge just the edited fields into what they already show.
  updateBooking: (bookingId: string, payload: BookingUpdatePayload) =>
    apiRequest<BookingResponse>(`/bookings/${encodeURIComponent(bookingId)}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),

  // Waiting list
  listWaitlistEntries: (params?: {
    professional_id?: string;
    service_id?: string;
    location_id?: string;
    source?: WaitlistSource;
    search?: string;
    status?: WaitlistStatusFilter;
  }) => {
    const search = new URLSearchParams();
    if (params?.professional_id) search.append("professional_id", params.professional_id);
    if (params?.service_id) search.append("service_id", params.service_id);
    if (params?.location_id) search.append("location_id", params.location_id);
    if (params?.status) search.append("status", params.status);
    if (params?.source) search.append("source", params.source);
    if (params?.search) search.append("search", params.search);
    const queryString = search.toString();
    return apiRequest<WaitlistEntryResponse[]>(
      `/waitlist-entries${queryString ? `?${queryString}` : ""}`
    );
  },

  getWaitlistStatusCounts: () =>
    apiRequest<WaitlistStatusCounts>("/waitlist-entries/status-counts"),

  // Entries made after a moment, with the server's own clock for the next cursor (the same
  // pattern as listAgentTasksSince): how the panel tells staff about a new waiting-list join.
  listWaitlistEntriesSince: async (params: {
    created_after: string;
    origin?: string;
  }): Promise<{ entries: WaitlistEntryResponse[]; serverTime: string | null }> => {
    const search = new URLSearchParams({ created_after: params.created_after });
    if (params.origin) search.append("origin", params.origin);
    const { data, headers } = await apiRequestWithHeaders<WaitlistEntryResponse[]>(
      `/waitlist-entries?${search.toString()}`
    );
    return { entries: data, serverTime: headers.get("x-server-time") };
  },

  // Earlier slots each active entry could take today, keyed by entry id. One availability search per
  // entry server-side, so the tab asks for it separately from the list.
  getWaitlistMatchCounts: () =>
    apiRequest<Record<string, number>>("/waitlist-entries/match-counts"),

  createWaitlistEntry: (payload: WaitlistEntryCreatePayload) =>
    apiRequest<WaitlistEntryResponse>("/waitlist-entries", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  updateWaitlistEntry: (waitlistEntryId: string, payload: WaitlistEntryUpdatePayload) =>
    apiRequest<WaitlistEntryResponse>(
      `/waitlist-entries/${encodeURIComponent(waitlistEntryId)}`,
      { method: "PATCH", body: JSON.stringify(payload) }
    ),

  /** Close the entry as fulfilled: the patient was booked or moved to a slot that suits them. */
  fulfillWaitlistEntry: (waitlistEntryId: string) =>
    apiRequest<WaitlistEntryResponse>(
      `/waitlist-entries/${encodeURIComponent(waitlistEntryId)}/fulfill`,
      { method: "POST" }
    ),

  deleteWaitlistEntry: (waitlistEntryId: string) =>
    apiRequest<void>(`/waitlist-entries/${encodeURIComponent(waitlistEntryId)}`, {
      method: "DELETE",
    }),

  // Which waiting patients a freed/candidate slot (professional + date + time) would
  // satisfy — powers the calendar's "N waiting" pill and matches drawer (PR2).
  findWaitlistMatches: (params: {
    date: string; // "YYYY-MM-DD"
    start_time: string; // "HH:MM", local to the clinic's timezone
    professional_id?: string;
    service_id?: string;
  }) => {
    const search = new URLSearchParams();
    search.append("date", params.date);
    search.append("start_time", params.start_time);
    if (params.professional_id) search.append("professional_id", params.professional_id);
    if (params.service_id) search.append("service_id", params.service_id);
    return apiRequest<WaitlistEntryResponse[]>(`/waitlist-entries/matches?${search.toString()}`);
  },

  // Slots
  searchSlots: (payload: SlotSearchPayload) =>
    apiRequest<SlotSearchResult>("/slots/search", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  // Activity Log
  listActivityLog: (params: { conversation_id: string }) =>
    apiRequest<ActivityLogEntry[]>(
      `/activity-log?conversation_id=${encodeURIComponent(params.conversation_id)}`
    ),

  // Agent Tasks
  listAgentTasks: (params: {
    conversation_id?: string;
    client_id?: string;
    status?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params.conversation_id)
      searchParams.append("conversation_id", params.conversation_id);
    if (params.client_id)
      searchParams.append("client_id", params.client_id);
    // Backend query param is `task_status`, not `status`. FastAPI silently
    // ignores unrecognized query params rather than 422ing, so this filter had
    // been dead since it was written — every call returned every status.
    if (params.status) searchParams.append("task_status", params.status);
    const queryString = searchParams.toString();
    return apiRequest<AgentTask[]>(
      `/agent-tasks${queryString ? `?${queryString}` : ""}`
    );
  },

  // Polls for tasks created after `created_after` (ISO timestamp). Returns the
  // server's own clock (X-Server-Time) alongside the results so callers can use
  // it as the next cursor without drifting on client/server clock skew.
  listAgentTasksSince: async (params: {
    created_after?: string;
    status?: string;
  }): Promise<{ tasks: AgentTask[]; serverTime: string | null }> => {
    const searchParams = new URLSearchParams();
    if (params.status) searchParams.append("task_status", params.status);
    if (params.created_after)
      searchParams.append("created_after", params.created_after);
    const queryString = searchParams.toString();
    const { data, headers } = await apiRequestWithHeaders<AgentTask[]>(
      `/agent-tasks${queryString ? `?${queryString}` : ""}`
    );
    return { tasks: data, serverTime: headers.get("x-server-time") };
  },

  getAgentTask: (taskId: string) =>
    apiRequest<AgentTask>(`/agent-tasks/${taskId}`),

  createAgentTask: (task: AgentTaskCreate) =>
    apiRequest<AgentTask>("/agent-tasks", {
      method: "POST",
      body: JSON.stringify(task),
    }),

  updateAgentTask: (taskId: string, updates: AgentTaskUpdate) =>
    apiRequest<AgentTask>(`/agent-tasks/${taskId}`, {
      method: "PATCH",
      body: JSON.stringify(updates),
    }),

  deleteAgentTask: (taskId: string) =>
    apiRequest<void>(`/agent-tasks/${taskId}`, {
      method: "DELETE",
    }),

  // Admin-only: the ToniAgent team's task queue, across every clinic.
  listAgentTeamTasks: (params: {
    status?: string;
    client_id?: string;
    conversation_id?: string;
  } = {}) => {
    const searchParams = new URLSearchParams();
    if (params.status) searchParams.append("task_status", params.status);
    if (params.client_id) searchParams.append("client_id", params.client_id);
    if (params.conversation_id)
      searchParams.append("conversation_id", params.conversation_id);
    const queryString = searchParams.toString();
    return apiRequest<AgentTeamTask[]>(
      `/admin/agent-tasks${queryString ? `?${queryString}` : ""}`
    );
  },

  countPendingAgentTeamTasks: () =>
    apiRequest<number>("/admin/agent-tasks/pending-count"),

  updateAgentTeamTaskStatus: (taskId: string, status: "pending" | "completed") =>
    apiRequest<AgentTeamTask>(`/admin/agent-tasks/${taskId}`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    }),

  // Agendas
  createAgenda: (data: CreateAgendaRequest) =>
    apiRequest<Agenda>("/agendas", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  listAgendas: (includeInactive: boolean = false) =>
    apiRequest<Agenda[]>(`/agendas${includeInactive ? '?include_inactive=true' : ''}`),

  getAgenda: (agendaId: string) =>
    apiRequest<Agenda>(`/agendas/${agendaId}`),

  /** Resolved open/close/breaks for each of the current client's professionals'
   * agenda(s) on one local calendar date, or (with dateTo) a range — one entry
   * per professional per date. Powers the Agendas calendar day/week views'
   * availability shading. */
  getAgendasDaySchedule: (params: { date: string; dateTo?: string }, options?: { signal?: AbortSignal }) => {
    const search = new URLSearchParams();
    search.append("date", params.date);
    if (params.dateTo) search.append("date_to", params.dateTo);
    return apiRequest<AgendaDaySchedule[]>(`/agendas/day-schedule?${search.toString()}`, options);
  },

  updateAgenda: (agendaId: string, data: UpdateAgendaRequest) =>
    apiRequest<Agenda>(`/agendas/${agendaId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  deleteAgenda: (agendaId: string, hard: boolean = false) =>
    apiRequest<void>(`/agendas/${agendaId}${hard ? '?hard=true' : ''}`, {
      method: "DELETE",
    }),

  restoreAgenda: (agendaId: string) =>
    apiRequest<Agenda>(`/agendas/${agendaId}/restore`, {
      method: "POST",
    }),

  /**
   * Bind a sync-managed agenda to a physical location (or unbind with null).
   * ClinicCloud/other integrations sync one agenda per site with location_id=null;
   * a human assigns each to a location. Errors are status-coded: 404 not found,
   * 409 the professional already has an active agenda at that location, 400 invalid/cross-client.
   */
  bindAgendaLocation: (agendaId: string, locationId: string | null) =>
    apiRequest<Agenda>(`/agendas/${agendaId}/location`, {
      method: "PUT",
      body: JSON.stringify({ location_id: locationId }),
    }),

  // Agenda Exceptions
  createAgendaException: (agendaId: string, data: CreateAgendaExceptionRequest) =>
    apiRequest<AgendaException>(`/agendas/${agendaId}/exceptions`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  listAgendaExceptions: (agendaId: string) =>
    apiRequest<AgendaException[]>(`/agendas/${agendaId}/exceptions`),

  deleteAgendaException: (agendaId: string, exceptionId: string) =>
    apiRequest<void>(`/agendas/${agendaId}/exceptions/${exceptionId}`, {
      method: "DELETE",
    }),

  /** Hides an exception that has ended from the panel's list; it stays on record. */
  archiveAgendaException: (agendaId: string, exceptionId: string) =>
    apiRequest<void>(`/agendas/${agendaId}/exceptions/${exceptionId}/archive`, {
      method: "POST",
    }),

  // Agenda Slot Rules
  createAgendaSlotRule: (agendaId: string, data: CreateAgendaSlotRuleRequest) =>
    apiRequest<AgendaSlotRule>(`/agendas/${agendaId}/slot-rules`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  listAgendaSlotRules: (agendaId: string) =>
    apiRequest<AgendaSlotRule[]>(`/agendas/${agendaId}/slot-rules`),

  updateAgendaSlotRule: (agendaId: string, ruleId: string, data: UpdateAgendaSlotRuleRequest) =>
    apiRequest<AgendaSlotRule>(`/agendas/${agendaId}/slot-rules/${ruleId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  deleteAgendaSlotRule: (agendaId: string, ruleId: string, hard: boolean = false) =>
    apiRequest<void>(`/agendas/${agendaId}/slot-rules/${ruleId}${hard ? '?hard=true' : ''}`, {
      method: "DELETE",
    }),

  // Agenda Day-Slot-Duration Overrides
  listAgendaDayDurations: (agendaId: string) =>
    apiRequest<AgendaDaySlotDuration[]>(`/agendas/${agendaId}/day-durations`),

  upsertAgendaDayDuration: (
    agendaId: string,
    dayOfWeek: number,
    data: UpsertAgendaDaySlotDurationRequest
  ) =>
    apiRequest<AgendaDaySlotDuration>(`/agendas/${agendaId}/day-durations/${dayOfWeek}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  deleteAgendaDayDuration: (agendaId: string, dayOfWeek: number) =>
    apiRequest<void>(`/agendas/${agendaId}/day-durations/${dayOfWeek}`, {
      method: "DELETE",
    }),

  // Google Calendar Integration
  startGoogleCalendarOAuth: (clientId?: string) => {
    const params = clientId ? `?client_id=${clientId}` : '';
    return apiRequest<OAuthStartResponse>(`/integrations/google-calendar/oauth/start${params}`);
  },

  completeGoogleCalendarOAuth: (data: OAuthCallbackRequest) =>
    apiRequest<GoogleAccountWithCalendars>(`/integrations/google-calendar/oauth/callback`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  listGoogleAccounts: (clientId?: string) => {
    const params = clientId ? `?client_id=${clientId}` : '';
    return apiRequest<GoogleAccountWithCalendars[]>(`/integrations/google-calendar/accounts${params}`);
  },

  getGoogleAccount: (accountId: string) =>
    apiRequest<GoogleAccountWithCalendars>(`/integrations/google-calendar/accounts/${accountId}`),

  getAvailableCalendars: (accountId: string) =>
    apiRequest<AvailableCalendarsResponse>(`/integrations/google-calendar/accounts/${accountId}/calendars/available`),

  selectCalendars: (accountId: string, calendarIds: string[]) =>
    apiRequest<CalendarIntegration[]>(`/integrations/google-calendar/accounts/${accountId}/calendars`, {
      method: "POST",
      body: JSON.stringify({ calendar_ids: calendarIds }),
    }),

  disconnectGoogleAccount: (accountId: string) =>
    apiRequest<void>(`/integrations/google-calendar/accounts/${accountId}`, {
      method: "DELETE",
    }),

  // Phase 2: Event Sync + Webhooks
  syncCalendarEvents: (integrationId: string) =>
    apiRequest<{ message: string }>(`/integrations/google-calendar/calendars/${integrationId}/sync`, {
      method: "POST",
    }),

  listCalendarEvents: (integrationId: string, params?: {
    start_time?: string;
    end_time?: string;
    page?: number;
    page_size?: number;
    sort?: "asc" | "desc";
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.start_time) queryParams.append('start_time', params.start_time);
    if (params?.end_time) queryParams.append('end_time', params.end_time);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    if (params?.sort) queryParams.append('sort', params.sort);
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return apiRequest<CalendarEventsListResponse>(`/integrations/google-calendar/calendars/${integrationId}/events${queryString}`);
  },

  disconnectCalendar: (integrationId: string) =>
    apiRequest<void>(`/integrations/google-calendar/calendars/${integrationId}`, {
      method: "DELETE",
    }),

  linkCalendarToAgenda: (agendaId: string, integrationId: string) =>
    apiRequest<Agenda>(`/agendas/${agendaId}`, {
      method: "PATCH",
      body: JSON.stringify({ calendar_integration_id: integrationId }),
    }),

  unlinkCalendarFromAgenda: (agendaId: string) =>
    apiRequest<Agenda>(`/agendas/${agendaId}`, {
      method: "PATCH",
      body: JSON.stringify({ calendar_integration_id: null }),
    }),

  // Agent Deployment
  deployAgent: () =>
    apiRequest<AgentDeploymentResponse>("/agents/deploy", {
      method: "POST",
    }),

  getAgentDeploymentStatus: () =>
    apiRequest<AgentDeploymentResponse | null>("/agents/deployment/status"),

  getAgentBuilderVersion: () =>
    apiRequest<{ builder_version: string }>("/agents/builder-version"),

  // Knowledge Base
  getKnowledgeBaseFiles: () =>
    apiRequest<KnowledgeBaseFileResponse[]>("/knowledge-base"),

  getKnowledgeBaseDownloadUrl: (fileId: string) =>
    apiRequest<{ url: string }>(`/knowledge-base/${fileId}/download-url`),

  getKnowledgeBaseUploadUrl: (data: KnowledgeBaseUploadUrlRequest) =>
    apiRequest<KnowledgeBaseUploadUrlResponse>("/knowledge-base/upload-url", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  confirmKnowledgeBaseUpload: (fileId: string) =>
    apiRequest<KnowledgeBaseFileResponse>(`/knowledge-base/${fileId}/confirm`, {
      method: "POST",
    }),

  deleteKnowledgeBaseFile: (fileId: string) =>
    apiRequest<null>(`/knowledge-base/${fileId}`, {
      method: "DELETE",
    }),

  renameKnowledgeBaseFile: (fileId: string, fileName: string) =>
    apiRequest<KnowledgeBaseFileResponse>(`/knowledge-base/${fileId}`, {
      method: "PATCH",
      body: JSON.stringify({ file_name: fileName }),
    }),

  getKnowledgeBaseVersions: (fileId: string) =>
    apiRequest<KnowledgeBaseFileVersionResponse[]>(`/knowledge-base/${fileId}/versions`),

  getKnowledgeBaseVersionUploadUrl: (fileId: string, data: KnowledgeBaseNewVersionRequest) =>
    apiRequest<KnowledgeBaseNewVersionResponse>(`/knowledge-base/${fileId}/versions/upload-url`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  confirmKnowledgeBaseVersion: (fileId: string, versionId: string) =>
    apiRequest<KnowledgeBaseFileResponse>(`/knowledge-base/${fileId}/versions/${versionId}/confirm`, {
      method: "POST",
    }),

  restoreKnowledgeBaseVersion: (fileId: string, versionId: string) =>
    apiRequest<KnowledgeBaseFileResponse>(`/knowledge-base/${fileId}/versions/${versionId}/restore`, {
      method: "POST",
    }),

  getKnowledgeBaseVersionDownloadUrl: (fileId: string, versionId: string) =>
    apiRequest<{ url: string }>(`/knowledge-base/${fileId}/versions/${versionId}/download-url`),

  // Pronunciation Dictionary
  getPronunciationDictionary: () =>
    apiRequest<PronunciationDictionaryFileResponse | null>("/pronunciation-dictionary"),

  getPronunciationDictionaryUploadUrl: (data: PronunciationDictionaryUploadUrlRequest) =>
    apiRequest<PronunciationDictionaryUploadUrlResponse>(
      "/pronunciation-dictionary/upload-url",
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    ),

  confirmPronunciationDictionaryUpload: (fileId: string) =>
    apiRequest<PronunciationDictionaryFileResponse>(
      `/pronunciation-dictionary/${fileId}/confirm`,
      { method: "POST" }
    ),

  deletePronunciationDictionary: (fileId: string) =>
    apiRequest<null>(`/pronunciation-dictionary/${fileId}`, {
      method: "DELETE",
    }),

  getPronunciationDictionaryDownloadUrl: (fileId: string) =>
    apiRequest<{ url: string }>(`/pronunciation-dictionary/${fileId}/download-url`),

  // Dashboard
  getDashboardV3Summary: (startDate: Date, endDate: Date) => {
    const params = new URLSearchParams({
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
    });
    return apiRequest<import("@/types/dashboard").DashboardV3SummaryResponse>(
      `/dashboard/summary?${params.toString()}`
    );
  },

  getDashboardCurrentCycleSummary: () =>
    apiRequest<import("@/types/dashboard").DashboardCurrentCycleSummaryResponse>(
      "/dashboard/summary"
    ),

  getDashboardAnalytics: (startDate: Date, endDate: Date) => {
    const params = new URLSearchParams({
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
    });
    return apiRequest<import("@/types/dashboard").AnalyticsResponse | null>(
      `/dashboard/analytics?${params.toString()}`
    );
  },

  updateRoiAssumptions: (data: import("@/types/dashboard").RoiAssumptionsUpdate) =>
    apiRequest<import("@/types/dashboard").RoiAssumptions>(
      "/dashboard/assumptions",
      { method: "PUT", body: JSON.stringify(data) }
    ),

  // Terms of Service
  /**
   * The terms version currently in force. Public — no session needed, because the signup
   * form and the /legal pages both need it before the user is authenticated.
   *
   * The backend is the single place the version is declared; nothing in this repo
   * hardcodes it.
   */
  getCurrentTerms: () =>
    publicApiRequest<CurrentTermsResponse>("/terms/current"),

  getTermsStatus: () =>
    apiRequest<TermsStatusResponse>("/auth/terms-status"),

  /** Admin register: every clinic and the terms version it accepted. */
  getTermsAcceptances: () =>
    apiRequest<TermsRegisterResponse>("/admin/terms-acceptances"),

  acceptTerms: (termsVersion: string) =>
    apiRequest<AcceptTermsResponse>("/auth/accept-terms", {
      method: "POST",
      body: JSON.stringify({ terms_version: termsVersion }),
    }),

  // Legal versions: what is in force, what is coming, and the owner's acknowledgment.
  // There is no blocking acceptance any more (Terms §15); the banner uses these.
  getLegalStatus: () => apiRequest<LegalStatusResponse>("/legal/status"),

  acknowledgeLegalVersion: (version: string) =>
    apiRequest<ClientLegalAcceptance>("/legal/acknowledge", {
      method: "POST",
      body: JSON.stringify({ version }),
    }),

  // The language the user picked in the panel, remembered so the legal-notice email
  // reaches them in it.
  getPreferredLanguage: () => apiRequest<PreferredLanguageResponse>("/auth/profile/language"),

  setPreferredLanguage: (language: string) =>
    apiRequest<PreferredLanguageResponse>("/auth/profile/language", {
      method: "PATCH",
      body: JSON.stringify({ language }),
    }),

  // Admin: schedule a version's go-live (which sends the announcement) and see who was told.
  getAdminLegalVersions: () => apiRequest<AdminLegalVersionsResponse>("/admin/legal/versions"),

  scheduleLegalVersion: (body: ScheduleLegalVersionRequest) =>
    apiRequest<ScheduleLegalVersionResponse>("/admin/legal/versions", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  previewLegalNotice: (body: LegalNoticePreviewRequest) =>
    apiRequest<LegalNoticePreviewResponse>("/admin/legal/notices/preview", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  sendTestLegalNotice: (body: LegalNoticePreviewRequest) =>
    apiRequest<LegalNoticeTestResponse>("/admin/legal/notices/test", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  getLegalNotices: (version: string) =>
    apiRequest<LegalNoticeRow[]>(`/admin/legal/versions/${encodeURIComponent(version)}/notices`),

  getLegalObjections: (version: string) =>
    apiRequest<LegalObjection[]>(
      `/admin/legal/versions/${encodeURIComponent(version)}/objections`,
    ),

  recordLegalObjection: (version: string, clientId: string, note: string) =>
    apiRequest<LegalObjection>(
      `/admin/legal/versions/${encodeURIComponent(version)}/objections`,
      { method: "POST", body: JSON.stringify({ client_id: clientId, note }) },
    ),

  resolveLegalObjection: (objectionId: string, outcome: LegalObjection["outcome"]) =>
    apiRequest<LegalObjection>(`/admin/legal/objections/${encodeURIComponent(objectionId)}`, {
      method: "PATCH",
      body: JSON.stringify({ outcome }),
    }),

  // Per-user objection to browser performance monitoring (New Relic RUM). The agent
  // is cookie-free and on by default, so this records an opt-out, not a consent.
  getMonitoringPreference: () =>
    apiRequest<MonitoringPreferenceResponse>("/auth/monitoring-preference"),

  setMonitoringPreference: (optedOut: boolean) =>
    apiRequest<MonitoringPreferenceResponse>("/auth/monitoring-preference", {
      method: "PUT",
      body: JSON.stringify({ opted_out: optedOut }),
    }),

  // Integrations — aggregated connection status for the integrations index page.
  // One request instead of one `/account` call per provider.
  getIntegrationConnections: () =>
    apiRequest<IntegrationConnections>("/integrations/connections"),

  // Archivex Integration
  getArchivexAccount: () =>
    apiRequest<ArchivexAccountResponse | null>("/integrations/archivex/account"),

  connectArchivex: (data: ArchivexConnectRequest) =>
    apiRequest<ArchivexAccountResponse>("/integrations/archivex/connect", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  disconnectArchivex: (keepData: boolean) =>
    apiRequest<void>(`/integrations/archivex/disconnect?keep_data=${keepData}`, {
      method: "DELETE",
    }),

  syncAllArchivex: () =>
    apiRequest<FullSyncReportResponse>("/integrations/archivex/sync-all", {
      method: "POST",
    }),

  // Docfav Integration
  getDocfavAccount: () =>
    apiRequest<DocfavAccountResponse | null>("/integrations/docfav/account"),

  connectDocfav: (data: DocfavConnectRequest) =>
    apiRequest<DocfavAccountResponse>("/integrations/docfav/connect", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  disconnectDocfav: (keepData: boolean) =>
    apiRequest<void>(`/integrations/docfav/disconnect?keep_data=${keepData}`, {
      method: "DELETE",
    }),

  syncAllDocfav: () =>
    apiRequest<DocfavSyncDispatchedResponse>("/integrations/docfav/sync", {
      method: "POST",
    }),

  listDocfavProfiles: (data: DocfavListProfilesRequest) =>
    apiRequest<DocfavProfilesResponse>("/integrations/docfav/profiles", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  // Gesden Integration
  // `clientId` lets an admin read another clinic's account (`asClient`).
  getGesdenAccount: (clientId?: string) =>
    apiRequest<GesdenAccountResponse | null>("/integrations/gesden/account", asClient(clientId)),

  connectGesden: (data: GesdenConnectRequest) =>
    apiRequest<GesdenAccountResponse>("/integrations/gesden/connect", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  disconnectGesden: (keepData: boolean) =>
    apiRequest<void>(`/integrations/gesden/disconnect?keep_data=${keepData}`, {
      method: "DELETE",
    }),

  setGesdenSqlBackupEnabled: (enabled: boolean) =>
    apiRequest<GesdenAccountResponse>("/integrations/gesden/sql-backup", {
      method: "PUT",
      body: JSON.stringify({ sql_backup_enabled: enabled }),
    }),

  // Admin-only. Both keys are always sent; `{ start: null, end: null }` clears it.
  setGesdenProbeQuietWindow: (clientId: string, window: GesdenProbeQuietWindow) =>
    apiRequest<GesdenAccountResponse>("/integrations/gesden/probe-quiet-window", {
      method: "PUT",
      body: JSON.stringify(window),
      ...asClient(clientId),
    }),

  syncAllGesden: () =>
    apiRequest<GesdenSyncDispatchedResponse>("/integrations/gesden/sync-all", {
      method: "POST",
    }),

  // Both db-check calls open a real SQL Server connection over Tailscale, and the
  // POST variant retries TDS versions newest-to-oldest when none is given. That
  // can outlast the default 30s, so give them their own budget - otherwise we'd
  // report a client-side timeout instead of the backend's "unreachable" verdict.
  checkGesdenDbWithCredentials: (data: GesdenDbCheckRequest) =>
    apiRequest<GesdenDbCheckResponse>(
      "/integrations/gesden/db-check",
      { method: "POST", body: JSON.stringify(data) },
      GESDEN_DB_CHECK_TIMEOUT_MS
    ),

  // Uses the stored credentials - the only way to re-check a connected clinic.
  checkGesdenDb: () =>
    apiRequest<GesdenDbCheckResponse>(
      "/integrations/gesden/db-check",
      {},
      GESDEN_DB_CHECK_TIMEOUT_MS
    ),

  // Serin Ocular IOM Integration
  getSerinIomAccount: () =>
    apiRequest<SerinIomAccountResponse | null>("/integrations/serin-iom/account"),

  connectSerinIom: (data: SerinIomConnectRequest) =>
    apiRequest<SerinIomAccountResponse>("/integrations/serin-iom/connect", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  disconnectSerinIom: (keepData: boolean) =>
    apiRequest<void>(`/integrations/serin-iom/disconnect?keep_data=${keepData}`, {
      method: "DELETE",
    }),

  syncAllSerinIom: () =>
    apiRequest<SyncDispatchedResponse>("/integrations/serin-iom/sync-all", {
      method: "POST",
    }),

  // Admin: learnings generation
  triggerLearningsGeneration: (body: {
    client_id: string;
    start_date: string;
    end_date: string;
  }) =>
    apiRequest<LearningsJobResponse>("/admin/learnings/jobs", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  listLearningsJobs: (clientId?: string) => {
    const params = new URLSearchParams();
    if (clientId) params.append("client_id", clientId);
    const qs = params.toString();
    return apiRequest<PaginatedLearningsJobsResponse>(
      `/admin/learnings/jobs${qs ? `?${qs}` : ""}`,
    );
  },

  getLearningsJob: (jobId: string) =>
    apiRequest<LearningsJobResponse>(`/admin/learnings/jobs/${jobId}`),

  getLearningsFileDownloadUrl: (jobId: string, fileId: string) =>
    apiRequest<{ url: string }>(
      `/admin/learnings/jobs/${jobId}/files/${fileId}/download`,
    ),

  // Clinic Cloud Integration
  getClinicCloudAccount: () =>
    apiRequest<ClinicCloudAccountResponse | null>("/integrations/clinic-cloud/account"),

  connectClinicCloud: (data: ClinicCloudConnectRequest) =>
    apiRequest<ClinicCloudAccountResponse>("/integrations/clinic-cloud/connect", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  disconnectClinicCloud: (keepData: boolean) =>
    apiRequest<void>(`/integrations/clinic-cloud/disconnect?keep_data=${keepData}`, {
      method: "DELETE",
    }),

  syncAllClinicCloud: () =>
    apiRequest<void>("/integrations/clinic-cloud/sync-all", {
      method: "POST",
    }),

  // Mediconta Integration
  getMedicontaAccount: () =>
    apiRequest<MedicontaAccountResponse | null>("/integrations/mediconta/account"),

  connectMediconta: (data: MedicontaConnectRequest) =>
    apiRequest<MedicontaAccountResponse>("/integrations/mediconta/connect", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  disconnectMediconta: (keepData: boolean) =>
    apiRequest<void>(`/integrations/mediconta/disconnect?keep_data=${keepData}`, {
      method: "DELETE",
    }),

  syncAllMediconta: () =>
    apiRequest<MedicontaSyncDispatchedResponse>("/integrations/mediconta/sync", {
      method: "POST",
    }),

  // RadioWeb Integration
  getRadiowebAccount: () =>
    apiRequest<RadiowebAccountResponse | null>("/integrations/radioweb/account"),

  connectRadioweb: (data: RadiowebConnectRequest) =>
    apiRequest<RadiowebAccountResponse>("/integrations/radioweb/connect", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  disconnectRadioweb: (keepData: boolean) =>
    apiRequest<void>(`/integrations/radioweb/disconnect?keep_data=${keepData}`, {
      method: "DELETE",
    }),

  syncAllRadioweb: () =>
    apiRequest<RadiowebSyncDispatchedResponse>("/integrations/radioweb/sync", {
      method: "POST",
    }),

  // Mindbody Integration
  getMindbodyAccount: () =>
    apiRequest<MindbodyAccountResponse | null>("/integrations/mindbody/account"),

  connectMindbody: (data: MindbodyConnectRequest) =>
    apiRequest<void>("/integrations/mindbody/connect", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  disconnectMindbody: (keepData: boolean) =>
    apiRequest<void>(`/integrations/mindbody/disconnect?keep_data=${keepData}`, {
      method: "DELETE",
    }),

  syncAllMindbody: () =>
    apiRequest<MindbodySyncDispatchedResponse>("/integrations/mindbody/sync", {
      method: "POST",
    }),

  // Flowww Integration
  getFlowwwAccount: () =>
    apiRequest<FlowwwAccountResponse | null>("/integrations/flowww/account"),

  listFlowwwCentres: (data: { api_key: string }) =>
    apiRequest<FlowwwCentre[]>("/integrations/flowww/centres", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  connectFlowww: (data: FlowwwConnectRequest) =>
    apiRequest<FlowwwAccountResponse>("/integrations/flowww/connect", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  disconnectFlowww: (keepData: boolean) =>
    apiRequest<void>(`/integrations/flowww/disconnect?keep_data=${keepData}`, {
      method: "DELETE",
    }),

  syncAllFlowww: () =>
    apiRequest<FlowwwSyncDispatchedResponse>("/integrations/flowww/sync", {
      method: "POST",
    }),

  // Podocat Integration
  getPodocatAccount: () =>
    apiRequest<PodocatAccountResponse | null>("/integrations/podocat/account"),

  connectPodocat: (data: PodocatConnectRequest) =>
    apiRequest<void>("/integrations/podocat/connect", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  disconnectPodocat: (keepData: boolean) =>
    apiRequest<void>(`/integrations/podocat/disconnect?keep_data=${keepData}`, {
      method: "DELETE",
    }),

  syncAllPodocat: () =>
    apiRequest<PodocatSyncDispatchedResponse>("/integrations/podocat/sync", {
      method: "POST",
    }),

  setPodocatPreferPortalNames: (enabled: boolean) =>
    apiRequest<PodocatAccountResponse>("/integrations/podocat/settings", {
      method: "PATCH",
      body: JSON.stringify({ prefer_portal_names: enabled }),
    }),

  // APClinic Integration
  getApclinicAccount: () =>
    apiRequest<ApclinicAccountResponse | null>("/integrations/apclinic/account"),

  connectApclinic: (data: ApclinicConnectRequest) =>
    apiRequest<ApclinicAccountResponse>("/integrations/apclinic/connect", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  disconnectApclinic: (keepData: boolean) =>
    apiRequest<void>(`/integrations/apclinic/disconnect?keep_data=${keepData}`, {
      method: "DELETE",
    }),

  syncAllApclinic: () =>
    apiRequest<ApclinicSyncDispatchedResponse>("/integrations/apclinic/sync", {
      method: "POST",
    }),

  // Doctolib Integration. Self-serve connect (initiate_connect) mirrors
  // Clinic Cloud's connect: submit credentials, validate live, persist — but
  // is currently incomplete server-side (always rejects for now, see
  // control-panel-api app/services/doctolib_connector.py) since completing
  // the verification-code step isn't wired in yet.
  getDoctolibAccount: () =>
    apiRequest<DoctolibAccountResponse | null>("/integrations/doctolib/account"),

  connectDoctolib: (data: DoctolibCredentialsRequest) =>
    apiRequest<void>("/integrations/doctolib/connect", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  storeDoctolibCredentials: (data: DoctolibCredentialsRequest) =>
    apiRequest<DoctolibAccountResponse>("/integrations/doctolib/credentials", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  disconnectDoctolib: (keepData: boolean) =>
    apiRequest<void>(`/integrations/doctolib/disconnect?keep_data=${keepData}`, {
      method: "DELETE",
    }),

  syncAllDoctolib: () =>
    apiRequest<SyncDispatchedResponse>("/integrations/doctolib/sync", {
      method: "POST",
    }),

  // Admin-only: the plaintext credentials an integration uses to connect, for
  // support/diagnosis. POST because the backend records an audit row per reveal,
  // and to keep secrets out of browser history and any GET cache. The response is
  // never cached client-side either — it lives only while the modal is open.
  revealIntegrationCredentials: (provider: IntegrationProvider) =>
    apiRequest<RevealedCredentialsResponse>(
      `/admin/integrations/${provider}/credentials/reveal`,
      { method: "POST", cache: "no-store" }
    ),

  // ── Client credentials (admin-only vault) ─────────────────────────────────
  // Client id lives in the path rather than the `X-Client-Id` header: the caller
  // is an admin on another clinic's page, not acting as that clinic.

  listClientCredentials: (clientId: string) =>
    apiRequest<ClientCredential[]>(`/admin/clients/${clientId}/credentials`),

  createClientCredential: (clientId: string, body: ClientCredentialCreate) =>
    apiRequest<ClientCredential>(`/admin/clients/${clientId}/credentials`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // Only send the fields the user actually changed: an omitted secret keeps the
  // stored value, an explicit null clears it.
  updateClientCredential: (
    clientId: string,
    credentialId: string,
    body: ClientCredentialUpdate,
  ) =>
    apiRequest<ClientCredential>(
      `/admin/clients/${clientId}/credentials/${credentialId}`,
      { method: "PATCH", body: JSON.stringify(body) },
    ),

  deleteClientCredential: (clientId: string, credentialId: string) =>
    apiRequest<void>(`/admin/clients/${clientId}/credentials/${credentialId}`, {
      method: "DELETE",
    }),

  // ── Client PBX extension (admin-only, singleton per client) ───────────────
  // The endpoint resolves its client via `get_client_id`, which honours
  // `X-Client-Id` for an admin — so, unlike the credentials vault above, the
  // client id rides the header (`asClient`) rather than the URL path.
  // 404 means "not configured yet", not an error — callers should treat it
  // as the empty state, not surface it as a failure.

  getClientPbxExtension: (clientId: string) =>
    apiRequest<ClientPbxExtension>("/client-pbx-extension", asClient(clientId)),

  updateClientPbxExtension: (clientId: string, body: ClientPbxExtensionUpsert) =>
    apiRequest<ClientPbxExtension>("/client-pbx-extension", {
      method: "PUT",
      body: JSON.stringify(body),
      ...asClient(clientId),
    }),

  // 409 (pbx_extension_still_bound) while a Fargate task is still bound: that
  // task fetches this row at boot, so it must be unbound and removed from infra first.
  deleteClientPbxExtension: (clientId: string) =>
    apiRequest<null>("/client-pbx-extension", {
      method: "DELETE",
      ...asClient(clientId),
    }),

  // Infra identity, not SIP config — a separate endpoint from the upsert above,
  // set once at onboarding (see infra/docs/deployment/onboard-pbx-bridge-tenant.md,
  // company repo). Pass null to unbind. The region is bound together with the name.
  updateClientPbxExtensionEcsServiceName: (
    clientId: string,
    ecsServiceName: string | null,
    ecsRegion: PbxBridgeRegion,
  ) =>
    apiRequest<ClientPbxExtension>("/client-pbx-extension/ecs-service-name", {
      method: "PUT",
      body: JSON.stringify({ ecs_service_name: ecsServiceName, ecs_region: ecsRegion }),
      ...asClient(clientId),
    }),

  // POST because the backend records an audit row per reveal, and to keep secrets
  // out of browser history and any GET cache. The response is never cached
  // client-side either — it lives only while the modal is open.
  revealClientCredential: (clientId: string, credentialId: string) =>
    apiRequest<RevealedClientCredential>(
      `/admin/clients/${clientId}/credentials/${credentialId}/reveal`,
      { method: "POST", cache: "no-store" },
    ),

  listClientCredentialEvents: (clientId: string, limit = 100) =>
    apiRequest<ClientCredentialEvent[]>(
      `/admin/clients/${clientId}/credential-events?limit=${limit}`,
    ),

  // ── Contacts ───────────────────────────────────────────────────

  listContacts: (params?: ListContactsParams) => {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.append("search", params.search);
    if (params?.kind) searchParams.append("kind", params.kind);
    if (params?.source) searchParams.append("source", params.source);
    if (params?.language) searchParams.append("language", params.language);
    if (params?.activity) searchParams.append("activity", params.activity);
    if (params?.include_facets) searchParams.append("include_facets", "true");
    if (params?.sort_key) searchParams.append("sort_key", params.sort_key);
    if (params?.sort_dir) searchParams.append("sort_dir", params.sort_dir);
    if (params?.skip !== undefined) searchParams.append("skip", params.skip.toString());
    if (params?.limit !== undefined) searchParams.append("limit", params.limit.toString());
    const queryString = searchParams.toString();
    return apiRequest<PaginatedContactsResponse>(
      `/contacts${queryString ? `?${queryString}` : ""}`
    );
  },

  /** The id of the contact on a phone line, for screens that know a patient only by
   *  number. The number travels in the body — never the URL. See `resolveContactId`. */
  lookupContact: (phone: string) =>
    apiRequest<{ id: string }>("/contacts/lookup", {
      method: "POST",
      body: JSON.stringify({ phone }),
    }),

  // Every contact endpoint below is addressed by the contact's `cont_<uuid>` id.
  getContact: (contactId: string) =>
    apiRequest<ContactDetail>(`/contacts/${encodeURIComponent(contactId)}`),

  /** The contact with its appointments in one request. */
  getContactWithAppointments: (contactId: string) =>
    apiRequest<ContactDetailWithAppointments>(
      `/contacts/${encodeURIComponent(contactId)}?include=appointments`
    ),

  /** Corrects one patient record on this line. A changed name also reaches the
   *  patient's bookings, which is what lets the agent find them again. */
  updateContactPatient: (contactId: string, patientId: string, body: ContactPatientEditBody) =>
    apiRequest<ContactPatientEditResponse>(
      `/contacts/${encodeURIComponent(contactId)}/patients/${encodeURIComponent(patientId)}`,
      { method: "PATCH", body: JSON.stringify(body) },
    ),

  getContactMessagePreferences: (contactId: string) =>
    apiRequest<ContactMessagePreferences>(
      `/contacts/${encodeURIComponent(contactId)}/message-preferences`,
    ),

  /** Changes only the preferences sent; the answer carries the new change history. */
  updateContactMessagePreferences: (
    contactId: string,
    body: Partial<Record<MessagePreferenceName, boolean>>,
  ) =>
    apiRequest<ContactMessagePreferences>(
      `/contacts/${encodeURIComponent(contactId)}/message-preferences`,
      { method: "PATCH", body: JSON.stringify(body) },
    ),

  /** Gives a contact with no patient its first one. The line is the path, not the body. */
  createContactPatient: (contactId: string, body: ContactPatientEditBody) =>
    apiRequest<ContactPatientEditResponse>(
      `/contacts/${encodeURIComponent(contactId)}/patients`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  /** Creates a contact for a number nobody has a record of. It has no id yet, so the phone
   *  travels in the body rather than the path. */
  createContact: (phone: string, body: ContactPatientEditBody) =>
    apiRequest<ContactPatientEditResponse>("/contacts", {
      method: "POST",
      body: JSON.stringify({ ...body, phone }),
    }),

  /** Every appointment on this line — future first, then past. Unpaginated: one
   *  patient's own history, small enough that the page paginates it client-side. */
  getContactAppointments: (contactId: string) =>
    apiRequest<ContactAppointment[]>(
      `/contacts/${encodeURIComponent(contactId)}/appointments`
    ),

  /** Sends this appointment's WhatsApp reminder and returns its new state.
   *  Idempotent server-side: a second call is refused rather than messaging twice. */
  sendAppointmentReminder: (contactId: string, appointmentId: string) =>
    apiRequest<ContactAppointment>(
      `/contacts/${encodeURIComponent(contactId)}/appointments/${encodeURIComponent(
        appointmentId
      )}/reminder`,
      { method: "POST" }
    ),

  // ── Patients ─────────────────────────────────────────────────────────────

  listPatients: (params?: ListPatientsParams) => {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.append("search", params.search);
    if (params?.include_deleted) searchParams.append("include_deleted", "true");
    const queryString = searchParams.toString();
    return apiRequest<PatientResponse[]>(`/patients${queryString ? `?${queryString}` : ""}`);
  },
};

export interface ClientTermsAcceptance {
  client_id: string;
  client_name: string;
  nif: string | null;
  address: string | null;
  zip_code: string | null;
  email: string | null;
  website: string | null;
  owner_email: string | null;
  client_created_at: string;
  terms_version: string | null;
  accepted_at: string | null;
  ip_address: string | null;
  source: string | null;
  accepted_by_email: string | null;
  accepted_by_role: string | null;
}

export interface TermsRegisterResponse {
  items: ClientTermsAcceptance[];
  current_version: string;
  current_effective_date: string;
  /**
   * Server-side generation timestamp for the certificate footer. Deliberately NOT
   * `new Date()` in the browser: the operator's machine clock is unverifiable, and this
   * date is printed on an evidentiary document.
   */
  generated_at: string;
}

export interface LegalVersionInfo {
  version: string;
  effective_date: string;
}

export interface UpcomingLegalVersionInfo extends LegalVersionInfo {
  highlights: Record<string, string[]>;
  announced_at: string | null;
}

export interface CurrentTermsResponse {
  version: string;
  effective_date: string;
  current: LegalVersionInfo;
  upcoming: UpcomingLegalVersionInfo | null;
}

export interface ClientLegalAcceptance {
  version: string;
  accepted_at: string;
  source: string;
  acknowledged_at: string | null;
}

export interface LegalStatusResponse {
  current: LegalVersionInfo;
  upcoming: UpcomingLegalVersionInfo | null;
  client_acceptance: ClientLegalAcceptance | null;
  /** The clinic's acceptance of the upcoming version, when it signed up on it already. */
  client_upcoming_acceptance: ClientLegalAcceptance | null;
}

export interface PreferredLanguageResponse {
  preferred_language: string | null;
}

export type LegalVersionState = "current" | "upcoming" | "superseded";

export interface AdminLegalVersion {
  version: string;
  effective_date: string;
  effective_at: string;
  notice_days: number;
  highlights: Record<string, string[]>;
  scheduled_at: string;
  scheduled_by: string | null;
  emergency_reason: string | null;
  announced_at: string | null;
  in_force_recorded_at: string | null;
  state: LegalVersionState;
  notices_sent: Record<string, number>;
  open_objections: number;
}

export interface LegalRecipientSummary {
  count: number;
  by_language: Record<string, number>;
}

export interface AdminLegalVersionsResponse {
  items: AdminLegalVersion[];
  current_version: string;
  earliest_effective_date: string;
  notice_days: number;
  recipients: LegalRecipientSummary;
}

export interface ScheduleLegalVersionRequest {
  version: string;
  effective_date: string;
  highlights: Record<string, string[]>;
  emergency_reason?: string | null;
}

export interface ScheduleLegalVersionResponse {
  version: AdminLegalVersion;
  announcements_enqueued: LegalRecipientSummary;
}

export interface LegalNoticePreviewRequest {
  version: string;
  effective_date: string;
  highlights: Record<string, string[]>;
  lang: string;
}

export interface LegalNoticePreviewResponse {
  subject: string;
  html: string;
  text: string;
}

export interface LegalNoticeTestResponse {
  sent_to: string;
  subject: string;
}

export interface LegalNoticeRow {
  id: string;
  version: string;
  email: string;
  client_id: string | null;
  kind: string;
  lang: string;
  claimed_at: string;
  sent_at: string | null;
  ses_message_id: string | null;
  error: string | null;
}

export interface LegalObjection {
  id: string;
  version: string;
  client_id: string;
  received_at: string;
  note: string;
  outcome: "open" | "withdrawn" | "terminated";
  recorded_by: string | null;
}

export interface TermsStatusResponse {
  requires_acceptance: boolean;
  current_version: string;
  user_accepted_version: string | null;
}

export interface AcceptTermsResponse {
  terms_version: string;
  accepted_at: string;
}

export interface MonitoringPreferenceResponse {
  opted_out: boolean;
  updated_at: string | null;
}

// Archivex Integration
export interface ArchivexAccountResponse {
  id: string;
  username: string;
  archivex_clinic_id: number | null;
  archivex_clinic_name: string | null;
  is_active: boolean;
  last_synced_at: string | null;
  sync_error: string | null;
}

export interface ArchivexConnectRequest {
  username: string;
  password: string;
}

// Docfav Integration
/** Which integrations the current client has connected — one flag per provider. */
export interface IntegrationConnections {
  google_calendar: boolean;
  whatsapp_business: boolean;
  archivex: boolean;
  serin_iom: boolean;
  docfav: boolean;
  clinic_cloud: boolean;
  gesden: boolean;
  mediconta: boolean;
  radioweb: boolean;
  apclinic: boolean;
  doctolib: boolean;
  mindbody: boolean;
  flowww: boolean;
  podocat: boolean;
}

export interface DocfavAccountResponse {
  id: string;
  back_office_clinic_id: string;
  is_active: boolean;
  last_synced_at: string | null;
  sync_error: string | null;
  created_at: string;
  updated_at: string;
}

export interface DocfavConnectRequest {
  api_key: string;
  back_office_clinic_id?: string;
}

export interface DocfavSyncDispatchedResponse {
  status: string;
  message_id: string | null;
}

export interface DocfavProfileItem {
  back_office_clinic_id: string;
  back_office_clinic_name: string;
  business_name: string | null;
  city: string | null;
  type: string;
  last_selected: boolean;
}

export interface DocfavProfilesResponse {
  profiles: DocfavProfileItem[];
}

export interface DocfavListProfilesRequest {
  api_key: string;
}

// Clinic Cloud Integration
export interface ClinicCloudAccountResponse {
  id: string;
  email: string;
  is_active: boolean;
  last_synced_at: string | null;
  sync_error: string | null;
}

export interface ClinicCloudConnectRequest {
  email: string;
  password: string;
  /** Only for clinics with 2FA (authenticator app) enabled on their Clinic Cloud account. */
  totp_secret?: string;
}

export interface SyncResultItem {
  created: number;
  updated: number;
  deleted: number;
}

// Doctolib Integration
export interface DoctolibAccountResponse {
  id: string;
  is_active: boolean;
  last_synced_at: string | null;
  sync_error: string | null;
  credentials_registered: boolean;
  login_in_progress: boolean;
}

export interface DoctolibCredentialsRequest {
  email: string;
  password: string;
  quick_unlock_pin: string;
}

// --- Admin: integration credentials (provider-agnostic) ---

// The PMS integrations that store connection credentials. Google Calendar is
// deliberately absent: it is OAuth, so there is nothing the clinic typed, and its
// tokens are per-user rather than per-client.
export type IntegrationProvider =
  | "apclinic"
  | "archivex"
  | "clinic_cloud"
  | "docfav"
  | "doctolib"
  | "flowww"
  | "gesden"
  | "mediconta"
  | "mindbody"
  | "radioweb"
  | "serin_iom";

export type RevealedFieldStatus = "revealed" | "empty" | "undecryptable";

export interface RevealedCredentialField {
  // Stable snake_case identifier, e.g. "api_key" or "host". Labels are supplied
  // by the frontend translations — the API has no i18n layer.
  key: string;
  // Populated only when status is "revealed": "empty" means the column is unset,
  // "undecryptable" means the encryption key changed after the row was written.
  value: string | null;
  secret: boolean;
  status: RevealedFieldStatus;
}

export interface RevealedCredentialsResponse {
  provider: string;
  client_id: string;
  // False for a disconnected integration — the reveal reads those rows on purpose.
  is_active: boolean;
  revealed_at: string;
  fields: RevealedCredentialField[];
}

// ── Client credentials (admin-only vault) ───────────────────────────────────
// The credentials WE hold for a clinic: switchboard portal, remote-access tool,
// on-premise machine, PMS login. Distinct from the *_accounts credentials the
// platform uses to talk to a PMS, which `revealIntegrationCredentials` covers.

export type ClientCredentialType =
  | "switchboard"
  | "remote_access"
  | "on_premise"
  | "pms"
  | "other";

export type ClientCredentialAction = "reveal" | "create" | "update" | "delete";

export interface ClientCredential {
  id: string;
  credential_type: ClientCredentialType;
  // The only hint about WHICH account this is that survives without a reveal —
  // username and password are both encrypted.
  public_comment: string | null;
  has_username: boolean;
  has_password: boolean;
  has_notes: boolean;
  created_by_email: string;
  updated_by_email: string | null;
  created_at: string;
  updated_at: string;
}

export interface ClientCredentialCreate {
  credential_type: ClientCredentialType;
  public_comment?: string | null;
  username?: string | null;
  password?: string | null;
  notes?: string | null;
}

/**
 * An omitted key means "leave it as it is"; an explicit `null` means "clear it".
 * Build these with care — sending `password: null` when the user simply did not
 * retype it wipes a working credential.
 */
export type ClientCredentialUpdate = Partial<ClientCredentialCreate>;

export interface RevealedClientCredential {
  id: string;
  credential_type: ClientCredentialType;
  revealed_at: string;
  fields: RevealedCredentialField[];
}

export interface ClientCredentialEvent {
  id: string;
  credential_id: string;
  credential_type: ClientCredentialType;
  action: ClientCredentialAction;
  // Field keys only, never values.
  fields: string[];
  actor_email: string;
  source_ip: string | null;
  created_at: string;
}

// ── Client PBX extension (admin-only, one row per client) ──────────────────
// The Avaya-side SIP credentials the Asterisk bridge registers with — see
// company/thoughts/shared/plans/2026-08-17-blade-avaya-sip-bridge.md.

// Ireland is where every bridge ran before cediv, whose firewall geo-blocks Irish IPs.
export const PBX_BRIDGE_REGIONS = ["eu-west-1", "eu-south-2"] as const;
export type PbxBridgeRegion = (typeof PBX_BRIDGE_REGIONS)[number];
export const DEFAULT_PBX_BRIDGE_REGION: PbxBridgeRegion = "eu-west-1";

export interface ClientPbxExtension {
  id: string;
  extension_number: string;
  registrar_fqdn: string;
  registrar_port: number;
  transport: "udp" | "tcp" | "tls";
  sip_domain: string;
  auth_username: string;
  // The secret itself is never sent to the browser — only whether one is stored.
  has_auth_secret: boolean;
  // Backend is str | None -- most existing rows have never set one.
  lan_route_tag: string | null;
  enabled: boolean;
  // Which pbx-bridge Fargate task (infra's own ECS service name) this clinic is
  // permanently bound to — null means no task has been assigned yet. Set via
  // updateClientPbxExtensionEcsServiceName, not the SIP-credential upsert above —
  // see infra/docs/deployment/onboard-pbx-bridge-tenant.md (company repo).
  ecs_service_name: string | null;
  // AWS region that task runs in. Optional only until the API that sends it is
  // deployed everywhere; absent means Ireland, the backend's default.
  ecs_region?: PbxBridgeRegion;
  // The one PBX extension a Twilio transfer-in call may target (see
  // control-panel-api's extensions.conf.template [from-twilio-transfer] gate) —
  // same single-value shape as clients.transfer_phone. Backend is str | None
  // -- null/unset means no transfer target is configured yet, and the
  // dialplan gate rejects every attempt until it is.
  transfer_extension: string | null;
  // When true, [bridge-to-twilio] skips Twilio entirely and dials the transfer
  // target directly — works regardless of this client's live/shadow status.
  direct_extension_transfer: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * `auth_secret` omitted means "keep the stored value" — the backend never
 * returns it, so there is nothing to leave unchanged otherwise. Required only
 * the first time a client is configured.
 */
export interface ClientPbxExtensionUpsert {
  extension_number: string;
  registrar_fqdn: string;
  registrar_port: number;
  transport: "udp" | "tcp" | "tls";
  sip_domain: string;
  auth_username: string;
  auth_secret?: string;
  // Backend is str | None (min_length=1 when present) -- "" fails that
  // validation, so this must stay optional/nullable, never a bare string.
  lan_route_tag?: string | null;
  enabled: boolean;
  // Same str | None shape and reasoning as lan_route_tag above.
  transfer_extension: string | null;
  direct_extension_transfer: boolean;
}

export interface FullSyncReportResponse {
  professionals: SyncResultItem & { agendas_created: number };
  services: SyncResultItem;
  professional_services_updated: number;
  patients: SyncResultItem;
  bookings: SyncResultItem;
}

// Learnings Generation
export type LearningsJobStatus =
  | "pending"
  | "queued"
  | "processing"
  | "completed"
  | "failed";

export interface LearningsFileResponse {
  id: string;
  file_type: string;
  file_name: string;
  created_at: string;
}

export interface LearningsJobResponse {
  id: string;
  client_id: string;
  version: number;
  start_date: string;
  end_date: string;
  status: LearningsJobStatus;
  progress_percentage: number;
  progress_message: string | null;
  error_message: string | null;
  triggered_by_email: string;
  transcriptions_count: number | null;
  total_batches: number | null;
  completed_batches: number;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  files: LearningsFileResponse[];
}

export interface PaginatedLearningsJobsResponse {
  items: LearningsJobResponse[];
  total: number;
  skip: number;
  limit: number;
}

// Agent health check
export type AgentHealthFindingSeverity = "error" | "warning";

/** Stable codes from the backend; the page maps them to translated messages. */
export type AgentHealthFindingCode =
  | "service_bookable_without_agent_professional"
  | "service_without_payment_type"
  | "professional_without_agenda"
  | "professional_without_bookable_service"
  | "professional_unusable_pms_id"
  | "no_availability_in_horizon"
  | "availability_horizon_very_short"
  | "no_patients"
  | "insurance_enabled_without_insurances"
  | "no_active_locations"
  | "no_agent_visible_professionals"
  | "no_agent_bookable_services"
  | "slot_rules_conflict";

export interface AgentHealthFinding {
  code: AgentHealthFindingCode;
  severity: AgentHealthFindingSeverity;
  entity_name: string | null;
  entity_id: string | null;
  detail: string | null;
}

export interface AgentHealthAgenda {
  id: string;
  source: string;
  location_name: string | null;
  slot_duration: number;
  slot_rule_count: number;
  exception_count: number;
  weekly_hours: string | null;
}

export interface AgentHealthProfessional {
  id: string;
  name: string;
  agent_visible: boolean;
  source: string;
  external_id: string | null;
  service_count: number;
  agent_bookable_service_count: number;
  agendas: AgentHealthAgenda[];
  slot_rule_count: number;
  exception_count: number;
}

export interface AgentHealthService {
  id: string;
  name: string;
  agent_visible: boolean;
  agent_bookable: boolean;
  professional_count: number;
  agent_visible_professional_count: number;
  payment_types: string[];
  source: string;
}

export interface AgentHealthCounts {
  patients: number;
  insurances: number;
  locations: number;
  professionals_total: number;
  professionals_agent_visible: number;
  services_total: number;
  services_agent_visible: number;
  services_agent_bookable: number;
  agendas: number;
  insurance_enabled: boolean;
}

export interface AgentHealthSummaryResponse {
  client_id: string;
  client_name: string;
  pms_sources: string[];
  horizon_days: number;
  counts: AgentHealthCounts;
  professionals: AgentHealthProfessional[];
  services: AgentHealthService[];
  findings: AgentHealthFinding[];
}

export interface AgentHealthServiceAvailability {
  service_id: string;
  service_name: string;
  payment_type: string | null;
  first_slot: string | null;
  last_slot: string | null;
  error: string | null;
  probe_truncated: boolean;
}

export interface AgentHealthAvailabilityResponse {
  professional_id: string;
  professional_name: string;
  first_slot: string | null;
  last_slot: string | null;
  horizon_days: number;
  /** The clinic's current date — the anchor the backend used for its own day counts. */
  today: string;
  searched_until: string;
  services: AgentHealthServiceAvailability[];
  findings: AgentHealthFinding[];
}

/** `unadjudicated` means the model could not be asked, not a third opinion. */
export type AgentHealthServiceNameVerdict =
  | "confusable"
  | "distinguishable"
  | "unadjudicated";

export interface AgentHealthConfusableServicePair {
  a_id: string;
  a_name: string;
  b_id: string;
  b_name: string;
  severity: AgentHealthFindingSeverity;
  verdict: AgentHealthServiceNameVerdict;
  /** Both services render the same name, so nothing at all tells them apart. */
  identical: boolean;
  both_bookable: boolean;
  reason: string | null;
  /** Computed by the backend: a PMS-synced name is restored by the nightly sync. */
  suggested_fix: string;
}

export interface AgentHealthServiceNamingResponse {
  client_id: string;
  pairs: AgentHealthConfusableServicePair[];
  /** Name strings compared, not services — a service renders one or two. */
  compared_name_count: number;
  candidates_truncated: boolean;
  llm_available: boolean;
}

// Gesden Integration
export type GesdenTdsVersion = "7.4" | "7.3" | "7.2" | "7.1" | "7.0";

export interface GesdenAccountResponse {
  id: string;
  host: string;
  port: number;
  database_name: string;
  username: string;
  is_active: boolean;
  last_synced_at: string | null;
  sync_error: string | null;
  tds_version: string | null;
  sql_backup_enabled: boolean;
  // Nightly window ("HH:MM", clinic local time) in which the reachability probe
  // neither checks nor alerts; start > end crosses midnight. Optional because
  // an API deployed before control-panel-api#2286 omits them.
  probe_quiet_start?: string | null;
  probe_quiet_end?: string | null;
  /** Whether this Gesden demands a birth date for new patients, read on every sync.
   * Null until it has been read. Optional until every deployed backend sends it. */
  requires_patient_birthdate?: boolean | null;
}

export interface GesdenProbeQuietWindow {
  start: string | null;
  end: string | null;
}

export interface GesdenConnectRequest {
  host: string;
  port?: number;
  database_name: string;
  username: string;
  password: string;
  // Omitted = backend auto-detects the TDS version at connect time.
  tds_version?: GesdenTdsVersion;
}

export interface GesdenSyncDispatchedResponse {
  status: string;
  message_id: string;
}

// Verdict of a Gesden database schema check. Report-only: a negative verdict
// arrives as HTTP 200 with a non-"compatible" status, never as an error.
export type SchemaCheckStatus =
  | "compatible"
  | "incompatible"
  | "unreachable"
  | "auth_failed";

export interface GesdenDbCheckRequest {
  host: string;
  port?: number;
  database_name: string;
  username: string;
  password: string;
  // Omitted/null = backend auto-detects and reports what it resolved.
  tds_version?: GesdenTdsVersion | null;
}

export interface GesdenMissingColumnResponse {
  table: string;
  column: string;
}

export interface GesdenTypeMismatchResponse {
  table: string;
  column: string;
  expected: string;
  found: string;
  found_sql_type: string;
}

export interface GesdenDbCheckResponse {
  status: SchemaCheckStatus;
  contract_version: string;
  host: string;
  database_name: string;
  tds_version: string | null;
  // Gesden's own version counters. Metadata only - never gate on them.
  ver_bbdd: number | null;
  ver_ps: number | null;
  tables_checked: number;
  missing_tables: string[];
  missing_columns: GesdenMissingColumnResponse[];
  type_mismatches: GesdenTypeMismatchResponse[];
  error: string | null;
  notes: string[];
}

// Serin Ocular IOM Integration
export interface SerinIomAccountResponse {
  id: string;
  protocol: "http" | "https";
  host: string;
  port: number;
  is_active: boolean;
  last_synced_at: string | null;
  sync_error: string | null;
  allow_self_signed_cert: boolean;
}

export interface SerinIomConnectRequest {
  protocol: "http" | "https";
  host: string;
  port: number;
  allow_self_signed_cert: boolean;
}

export interface SyncDispatchedResponse {
  status: string;
  message_id: string;
}

// Mediconta Integration
export interface MedicontaAccountResponse {
  id: string;
  is_active: boolean;
  last_synced_at: string | null;
  sync_error: string | null;
}

export interface MedicontaConnectRequest {
  bearer_token: string;
}

export interface MedicontaSyncDispatchedResponse {
  status: string;
  message_id: string | null;
}

// RadioWeb Integration
export interface RadiowebAccountResponse {
  id: string;
  base_url: string;
  username: string;
  default_insurance_lid: string | null;
  is_active: boolean;
  last_synced_at: string | null;
  sync_error: string | null;
}

export interface RadiowebConnectRequest {
  base_url: string;
  username: string;
  password: string;
  default_insurance_lid?: string | null;
}

export interface RadiowebSyncDispatchedResponse {
  status: string;
  message_id: string | null;
}

// Mindbody Integration
export interface MindbodyAccountResponse {
  id: string;
  is_active: boolean;
  last_synced_at: string | null;
  sync_error: string | null;
  login_in_progress: boolean;
}

export interface MindbodyConnectRequest {
  subscriber_id: string;
  username: string;
  password: string;
}

export interface MindbodySyncDispatchedResponse {
  status: string;
  message_id: string | null;
}

// Flowww Integration
export interface FlowwwAccountResponse {
  id: string;
  system_key: string | null;
  clinic_ids: number[];
  is_active: boolean;
  last_synced_at: string | null;
  sync_error: string | null;
}

/** A Flowww centre the key can sync; a Toni clinic is bound to one. */
export interface FlowwwCentre {
  id: number;
  name: string;
  city: string | null;
}

export interface FlowwwConnectRequest {
  api_key: string;
  system_key?: string | null;
  /** Flowww centres to sync; omitted or empty syncs every centre the key can see. */
  clinic_ids?: number[];
}

export interface FlowwwSyncDispatchedResponse {
  status: string;
  message_id: string | null;
}

// Podocat Integration
export interface PodocatAccountResponse {
  id: string;
  email: string;
  is_active: boolean;
  login_in_progress: boolean;
  last_synced_at: string | null;
  sync_error: string | null;
  prefer_portal_names: boolean;
}

export interface PodocatConnectRequest {
  email: string;
  password: string;
}

export interface PodocatSyncDispatchedResponse {
  status: string;
  message_id: string | null;
}

// APClinic Integration
export interface ApclinicAccountResponse {
  id: string;
  email: string;
  is_active: boolean;
  last_synced_at: string | null;
  sync_error: string | null;
}

export interface ApclinicConnectRequest {
  email: string;
  password: string;
}

export interface ApclinicSyncDispatchedResponse {
  status: string;
  message_id: string | null;
}

// ── Contacts ─────────────────────────────────────────────────────────────────
//
// A contact is a distinct PHONE LINE for a clinic, not a person: it carries
// 0..n patient identities and 0..1 caller record. Families legitimately share
// one number, so `display_names` is a list and must never be collapsed to one
// name in the UI.

/** `lead` is the set nothing in the product could show before: called, never a patient. */
export type ContactKind = "patient" | "lead" | "shared";

export interface Contact {
  /** `cont_<uuid>` — what every contact endpoint and URL is addressed by. */
  id: string;
  /** Display form — the E.164 one when any source has it. */
  phone: string;
  /** Digits-only key of the phone line this contact is. For matching (e.g. calls), never
   *  for addressing: a phone number does not belong in a URL. */
  phone_key: string;
  kind: ContactKind;
  patient_count: number;
  display_names: string[];
  sources: string[];
  has_caller_record: boolean;
  call_count: number;
  last_call_at: string | null;
  preferred_language: string | null;
  do_not_contact: boolean;
}

export interface ContactPatientIdentity {
  id: string;
  name: string;
  source: string;
  external_id: string | null;
  email: string | null;
  birth_date: string | null;
  preferred_language: string | null;
  do_not_contact: boolean;
  /** Absent until the API that stores them is deployed. */
  dni?: string | null;
  phone_secondary?: string | null;
  notes?: string | null;
  /** Absent until the API that returns it is deployed. */
  created_at?: string | null;
  /** CatSalut health card id; absent until the API that returns it is deployed. */
  cip?: string | null;
}

/** A partial edit of one patient record; `null` clears an optional field. */
export interface ContactPatientEditBody {
  name?: string;
  phone?: string;
  phone_secondary?: string | null;
  dni?: string | null;
  /** ISO date, YYYY-MM-DD. */
  birth_date?: string | null;
  email?: string | null;
  notes?: string | null;
}

export interface ContactPatientEditResponse {
  identity: ContactPatientIdentity;
  /** Where the line is now — different from the one edited when the phone changed. */
  phone_key: string;
  bookings_renamed: number;
}

export interface ContactDetail {
  contact: Contact;
  identities: ContactPatientIdentity[];
}

/** `GET /contacts/{id}?include=appointments`: the appointments are exactly what
 *  `getContactAppointments` returns. */
export interface ContactDetailWithAppointments extends ContactDetail {
  appointments: ContactAppointment[];
}

/** Why a reminder cannot be sent. A wire contract with the API's
 *  `ReminderBlockedReason` — each maps to a translated string. */
export type ReminderBlockedReason =
  | "past"
  | "already_reminded"
  | "already_confirmed"
  | "whatsapp_not_connected"
  | "agent_not_deployed"
  | "confirmation_requests_disabled"
  | "service_reminders_disabled"
  | "do_not_contact"
  | "reminders_opted_out"
  | "whatsapp_opted_out"
  | "no_phone"
  | "landline";

/** How a phone line asked to be messaged. It holds for every patient on the line. */
export type MessagePreferenceName = "whatsapp_opt_out" | "sms_opt_out" | "reminders_opt_out";

/** One change to a line's message preferences: who made it, when, and the new values.
 *  `changed_by` "staff" is a clinic user (`staff_email`); "toniagent" our team; "caller" the
 *  person on the line, asking the agent during a call (`call_id`, when that call was recorded). */
export interface MessagePreferenceChange {
  changed_at: string;
  changed_by: "staff" | "toniagent" | "caller" | "system";
  staff_email: string | null;
  changes: Partial<Record<MessagePreferenceName, boolean>>;
  call_id?: string | null;
}

/** `GET|PATCH /contacts/{id}/message-preferences`: the line's preferences and its latest
 *  changes, newest first. */
export type ContactMessagePreferences = Record<MessagePreferenceName, boolean> & {
  changes: MessagePreferenceChange[];
};

export interface ContactAppointment {
  appointment: BookingResponse;
  /** When the reminder actually went out. For a PMS-mirror appointment this comes from
   *  the WhatsApp ledger, since `bookings.reminder_sent_at` exists only on the native
   *  table. Null = never reminded (a failed send does not count). */
  reminder_sent_at: string | null;
  confirmed_at: string | null;
  /** When the patient answered that they cannot come — distinct from `confirmed_at`
   *  being null, which is equally true of a patient who never answered at all. */
  declined_at: string | null;
  /** Computed server-side; the button renders it rather than re-deriving the rules. */
  can_send_reminder: boolean;
  reminder_blocked_reason: ReminderBlockedReason | null;
}

export interface PaginatedContactsResponse {
  items: Contact[];
  total: number;
  skip: number;
  limit: number;
  /** Per-kind totals under the same search and filters, without the tab itself. */
  kind_counts?: Partial<Record<ContactKind, number>>;
  /** Only when `include_facets` was asked for. */
  facets?: ContactFacets | null;
}

/** The list columns a contact list can be ordered by — the design mock's own sort keys. */
export type ContactSortKey =
  | "contact"
  | "phone"
  | "type"
  | "calls"
  | "activity"
  | "source"
  | "language";

export type ContactSortDirection = "asc" | "desc";

/** "Last activity" filter: the clinic's own day, or the last seven days. */
export type ContactActivityWindow = "today" | "week";

/** What the Source and Language filters can be set to for this clinic. */
export interface ContactFacets {
  sources: string[];
  languages: string[];
}

export interface ListContactsParams {
  search?: string;
  kind?: ContactKind;
  /** Only contacts with a patient record from this source. */
  source?: string;
  language?: string;
  activity?: ContactActivityWindow;
  /** Also return `facets`; ask for it once, on the first load. */
  include_facets?: boolean;
  /** Named after the mock's `sortKey`/`sortDir` state. */
  sort_key?: ContactSortKey;
  sort_dir?: ContactSortDirection;
  skip?: number;
  limit?: number;
}

// ── Patients ─────────────────────────────────────────────────────────────────

export interface PatientResponse {
  id: string;
  client_id: string;
  phone: string | null;
  name: string;
  email: string | null;
  birth_date: string | null;
  gender: string;
  notes: string | null;
  preferred_language: string | null;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
  source: string;
  external_id: string | null;
}

export interface ListPatientsParams {
  search?: string;
  include_deleted?: boolean;
}
